import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Line, Chart, Bar } from 'react-chartjs-2';
import { useTheme } from '@/contexts/ThemeContext';
import { Header } from '@/components/shared';
import { generateCommands } from '@/config/commands';
import { useModals } from '@/contexts/ModalContext';
import { safeJsonParse } from '@/utils/apiUtils';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    Filler,
} from 'chart.js';
import DynamicAttribution from '@/components/shared/DynamicAttribution';
import ChartCategoryFilter from '@/components/shared/ChartCategoryFilter';
import { ENABLED_PROVIDER_IDS, TERRA_PROVIDER_IDS, getConnectedProviderIds, getTerraSourceLabel } from '@/config/wearableProviders';
import DataSourceFilter from '@/components/shared/DataSourceFilter';
import { HeartPulse, BarChart3, TrendingUp, TrendingDown, ClipboardList, Dumbbell, Settings, Watch, Moon } from '@/utils/icons';
import { LayoutGrid, Heart, Flame, Activity, Thermometer } from 'lucide-react';
import DashboardLayout from '@/components/shared/DashboardLayout';
import AthleteStatusCard from '@/components/athlete-detail/AthleteStatusCard';
import OverlayExportWrapper from '@/components/shared/OverlayExportWrapper';
import { shiftDateForward, generateDateRange } from '@/utils/dateUtils';

const PERSONAL_CHART_CATEGORIES = [
    { id: 'all', label: 'All', icon: LayoutGrid },
    { id: 'sleep', label: 'Sleep', icon: Moon },
    { id: 'recovery', label: 'Recovery', icon: Heart },
    { id: 'activity', label: 'Activity', icon: Flame },
    { id: 'cardio', label: 'Cardio', icon: Activity },
    { id: 'body', label: 'Body', icon: Thermometer },
];

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    Filler
);

/**
 * Interface for HRV data points from the OAuth API
 */
interface HRVDataPoint {
    date: string;
    hrv_value: number;
    hrv_rmssd?: number;  // Alternative property name for backward compatibility
    hrv?: number;        // Fallback property name
    value?: number;      // Additional fallback
    recovery_score?: number;
    resting_heart_rate?: number;
    respiratory_rate?: number;
    created_at?: string;
}

/**
 * Interface for Sleep data points from the OAuth API
 */
interface SleepDataPoint {
    date: string;
    sleep_performance_percentage: number;
    sleep_consistency_percentage: number;
    sleep_efficiency_percentage: number;
    total_sleep_time_milli: number;
    total_awake_time_milli: number;
    total_no_data_time_milli: number;
    total_light_sleep_time_milli: number;
    total_slow_wave_sleep_time_milli: number;
    total_rem_sleep_time_milli: number;
    sleep_cycle_count: number;
    disturbance_count: number;
    created_at: string;
}

/**
 * Interface for Activity data points from the OAuth API
 */
interface ActivityDataPoint {
    date: string;
    activity_type: string;
    duration_minutes: number;
    strain: number;
    kilojoule: number;
    average_heart_rate: number;
    max_heart_rate: number;
    distance_meter: number;  // Optional - not provided by cycles endpoint
    altitude_gain_meter: number;  // Optional - not provided by cycles endpoint
    percent_recorded: number;
    zone_duration: {
        zone_zero_milli: number;
        zone_one_milli: number;
        zone_two_milli: number;
        zone_three_milli: number;
        zone_four_milli: number;
        zone_five_milli: number;
    };
    created_at: string;
    calories_burned: number;  // Added for calorie display
    source: string;  // Added to track data source (daily_cycle, workout, etc.)
    duration_hours?: number;  // Optional - backend may provide this
}

/**
 * Interface for Sleep data summary
 */
interface SleepDataSummary {
    count: number;
    avg_sleep_score: number;
    min_sleep_score: number;
    max_sleep_score: number;
    avg_sleep_hours: number;
    min_sleep_hours: number;
    max_sleep_hours: number;
    latest_sleep_score: number;
    latest_sleep_hours: number;
    message?: string;
}

/**
 * Interface for Activity data summary
 */
interface ActivityDataSummary {
    count: number;
    avg_strain: number;
    min_strain: number;
    max_strain: number;
    avg_duration: number;
    min_duration: number;
    max_duration: number;
    latest_strain: number;
    latest_duration: number;
    message?: string;
}

/**
 * Interface for user information
 */
interface UserInfo {
    username: string;
    role?: string;
    full_name?: string;
    organization?: string;
    first_name?: string;
    whoop_connected?: boolean;
    garmin_connected?: boolean;
    coros_connected?: boolean;
    terra_connected?: { [provider: string]: boolean };
}

/**
 * Interface for Terra provider data from the API (Garmin, COROS, etc.)
 */
interface TerraData {
    distance_data: Array<{ date: string; distance_miles: number }>;
    rhr_data: Array<{ date: string; resting_hr: number }>;
    hr_zone_data: Array<{
        date: string;
        zone_1_minutes: number;
        zone_2_minutes: number;
        zone_3_minutes: number;
        zone_4_minutes: number;
        zone_5_minutes: number;
        total_minutes: number;
    }>;
    weekly_data: Array<{
        week_start_date: string;
        weekly_total: number;
        days_count: number;
        rolling_average?: number;
        weeks_in_window?: number;
    }>;
    sleep_efficiency_data: Array<{
        date: string;
        efficiency: number;
        score?: number;
        source: string;
    }>;
    sleep_stages_data: Array<{
        date: string;
        light_sleep_minutes: number;
        deep_sleep_minutes: number;
        rem_sleep_minutes: number;
        total_sleep_minutes: number;
        source: string;
    }>;
    respiratory_rate_data: Array<{
        date: string;
        breaths_per_min: number;
    }>;
    body_temp_data: Array<{
        date: string;
        temp_celsius: number;
        deviation: number | null;
    }>;
    stats: {
        average_distance_miles: number;
        total_distance_miles: number;
        average_rhr: number;
        latest_rhr: number;
        baseline_rhr: number | null;
        distance_data_points: number;
        rhr_data_points: number;
        average_sleep_efficiency: number;
        sleep_efficiency_data_points: number;
    };
    terra_connected: boolean;
}

/**
 * Elegant Personal Dashboard component with modern design and beautiful UI.
 * Features sophisticated data visualization, smooth animations, and intuitive user experience.
 * Displays personalized HRV data with advanced analytics and insights.
 */
const Dashboard: React.FC = () => {
    const [hrvData, setHrvData] = useState<HRVDataPoint[]>([]);
    const [sleepData, setSleepData] = useState<SleepDataPoint[]>([]);
    const [sleepSummary, setSleepSummary] = useState<SleepDataSummary | null>(null);
    const [activityData, setActivityData] = useState<ActivityDataPoint[]>([]);
    const [activitySummary, setActivitySummary] = useState<ActivityDataSummary | null>(null);
    const [terraData, setTerraData] = useState<TerraData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [days, setDays] = useState<number>(30);
    const [showDebugPanel, setShowDebugPanel] = useState<boolean>(false);
    const [selectedDataSources, setSelectedDataSources] = useState<string[]>(ENABLED_PROVIDER_IDS);
    const [debugInfo, setDebugInfo] = useState<{
        lastHrvCall?: { timestamp: string; duration: number; status: string; };
        lastSleepCall?: { timestamp: string; duration: number; status: string; };
        lastActivityCall?: { timestamp: string; duration: number; status: string; };
        lastGarminCall?: { timestamp: string; duration: number; status: string; };
        lastUserCall?: { timestamp: string; duration: number; status: string; };
        networkStatus: string;
    }>({ networkStatus: 'Unknown' });
    const [chartCategory, setChartCategory] = useState('all');
    const showCategory = (cat: string) => chartCategory === 'all' || chartCategory === cat;
    const menuRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();
    const { isDark } = useTheme();
    const { openCreateUserModal, openInviteUserModal, openCreateTeamModal, openCreateOrgModal } = useModals();

    // Derive which providers are connected for this user
    const connectedSources = useMemo(() => {
        if (!userInfo) return [];
        return getConnectedProviderIds(userInfo);
    }, [userInfo]);

    // Keep selectedDataSources in sync with connected providers
    // Skip when connectedSources is empty (userInfo hasn't loaded yet) to avoid
    // clearing selections before we know which providers are actually connected.
    useEffect(() => {
        if (connectedSources.length === 0) {
            return;
        }
        const filtered = selectedDataSources.filter((s: string) => connectedSources.includes(s));
        const next = filtered.length > 0 ? filtered : connectedSources;
        if (next.length !== selectedDataSources.length || next.some((s: string, i: number) => s !== selectedDataSources[i])) {
            setSelectedDataSources(next);
        }
    }, [connectedSources]);

    // Reactive terra data fetching — always passes the selected providers filter
    // This replaces the standalone fetchTerraData() call in the mount effect and
    // the inline refetch in onSourceChange, ensuring consistent filtering.
    useEffect(() => {
        const token = localStorage.getItem('access');
        if (!token) return;

        const terraProviders = selectedDataSources.filter(s => TERRA_PROVIDER_IDS.includes(s));
        if (terraProviders.length > 0) {
            fetchTerraData(terraProviders);
        } else {
            setTerraData(null);
        }
    }, [selectedDataSources, days]);

    /**
     * Effect to fetch user info and HRV data
     */
    useEffect(() => {
        // Check if user is authenticated
        const token = localStorage.getItem('access');
        if (!token) {
            navigate('/login');
            return;
        }

        // Get user info from localStorage
        const userInfoString = localStorage.getItem('user_info');
        if (userInfoString) {
            try {
                const user: UserInfo = JSON.parse(userInfoString);
                setUserInfo(user);
            } catch (err) {
                console.error('Error parsing user info:', err);
            }
        }

        fetchHRVData();
        fetchSleepData();
        fetchActivityData();
        // Note: fetchTerraData is handled by a dedicated useEffect that reacts to selectedDataSources
        refreshUserInfo();
    }, [navigate, days]);

    /**
     * Test connectivity on initial load
     */
    useEffect(() => {
        testConnectivity();
    }, []);

    /**
     * Effect to handle click outside menu
     */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    /**
     * Fetch HRV data from API
     */
    const fetchHRVData = async (): Promise<void> => {
        const startTime = Date.now();
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('access');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await fetch(`/api/hrv/user/?days=${days}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const elapsed = Date.now() - startTime;

            // Update debug info
            setDebugInfo(prev => ({
                ...prev,
                lastHrvCall: {
                    timestamp: new Date().toLocaleTimeString(),
                    duration: elapsed,
                    status: `${response.status} ${response.statusText}`
                },
                networkStatus: 'Connected'
            }));

            if (response.status === 401) {
                localStorage.removeItem('access');
                localStorage.removeItem('refresh');
                navigate('/login');
                return;
            }

            // Handle 404 as "no data available" rather than an error
            if (response.status === 404) {
                setHrvData([]);
                // If we get 404, also refresh user info to check connection status
                await refreshUserInfo();
                return;
            }

            // Handle 400 as "no data available" rather than an error (common when user has no HRV data)
            if (response.status === 400) {
                // Instead of setting an error, just clear the data and refresh user info
                // The "No HRV Data Available" section will handle the display appropriately
                setHrvData([]);
                await refreshUserInfo();
                return;
            }

            if (!response.ok) {
                const result = await safeJsonParse(response, 'fetching HRV data (error)');
                throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await safeJsonParse<any>(response, 'fetching HRV data');
            if (!result.success) {
                // If it's an HTML response (CloudFront routing issue), treat as "no data"
                // The empty state UI will show the connection prompt
                if (result.shouldRetry) {
                    setHrvData([]);
                    await refreshUserInfo();
                    return;
                }
                throw new Error(result.error || 'Failed to parse HRV data');
            }

            const data = result.data;

            // Handle new OAuth API response structure that has 'data' and 'summary' properties
            const hrvArray = Array.isArray(data) ? data : (data.data || []);
            setHrvData(hrvArray);
        } catch (err) {
            const elapsed = Date.now() - startTime;
            console.error(`Dashboard: Error after ${elapsed}ms:`, err);

            // Update debug info with error
            setDebugInfo(prev => ({
                ...prev,
                lastHrvCall: {
                    timestamp: new Date().toLocaleTimeString(),
                    duration: elapsed,
                    status: `ERROR: ${err instanceof Error ? err.message : 'Unknown error'}`
                },
                networkStatus: 'Error'
            }));

            setError(err instanceof Error ? err.message : 'An error occurred while fetching HRV data');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetch sleep data from API
     */
    const fetchSleepData = async (): Promise<void> => {
        const startTime = Date.now();
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('access');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await fetch(`/api/wearables/sleep/?days=${days}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const elapsed = Date.now() - startTime;

            // Update debug info
            setDebugInfo(prev => ({
                ...prev,
                lastSleepCall: {
                    timestamp: new Date().toLocaleTimeString(),
                    duration: elapsed,
                    status: `${response.status} ${response.statusText}`
                },
                networkStatus: 'Connected'
            }));

            if (response.status === 401) {
                localStorage.removeItem('access');
                localStorage.removeItem('refresh');
                navigate('/login');
                return;
            }

            // Handle 404 as "no data available" rather than an error
            if (response.status === 404) {
                setSleepData([]);
                setSleepSummary(null);
                // If we get 404, also refresh user info to check connection status
                await refreshUserInfo();
                return;
            }

            // Handle 400 as "no data available" rather than an error (common when user has no sleep data)
            if (response.status === 400) {
                // Instead of setting an error, just clear the data and refresh user info
                setSleepData([]);
                setSleepSummary(null);
                await refreshUserInfo();
                return;
            }

            if (!response.ok) {
                const result = await safeJsonParse(response, 'fetching sleep data (error)');
                throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await safeJsonParse<any>(response, 'fetching sleep data');
            if (!result.success) {
                // If it's an HTML response (CloudFront routing issue), treat as "no data"
                // The empty state UI will show the connection prompt
                if (result.shouldRetry) {
                    setSleepData([]);
                    setSleepSummary(null);
                    await refreshUserInfo();
                    return;
                }
                throw new Error(result.error || 'Failed to parse sleep data');
            }

            const data = result.data;

            // Handle API response structure that has 'data' and 'summary' properties
            const sleepArray = Array.isArray(data) ? data : (data.data || []);
            const summary = data.summary || null;

            // Remove duplicates based on date
            const uniqueSleepArray = sleepArray.reduce((acc: SleepDataPoint[], current: SleepDataPoint) => {
                const x = acc.find((item: SleepDataPoint) => item.date === current.date);
                if (!x) {
                    return acc.concat([current]);
                } else {
                    // If duplicate found, keep the one with the most recent created_at
                    if (new Date(current.created_at) > new Date(x.created_at)) {
                        return acc.map((item: SleepDataPoint) => item.date === current.date ? current : item);
                    }
                    return acc;
                }
            }, []);

            setSleepData(uniqueSleepArray);
            setSleepSummary(summary);

        } catch (err) {
            const elapsed = Date.now() - startTime;
            console.error(`Dashboard: Error after ${elapsed}ms:`, err);

            // Update debug info with error
            setDebugInfo(prev => ({
                ...prev,
                lastSleepCall: {
                    timestamp: new Date().toLocaleTimeString(),
                    duration: elapsed,
                    status: `ERROR: ${err instanceof Error ? err.message : 'Unknown error'}`
                },
                networkStatus: 'Error'
            }));

            setError(err instanceof Error ? err.message : 'An error occurred while fetching sleep data');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetch activity data from API
     */
    const fetchActivityData = async (): Promise<void> => {
        const startTime = Date.now();
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('access');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await fetch(`/api/wearables/activity/?days=${days}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const elapsed = Date.now() - startTime;

            // Update debug info
            setDebugInfo(prev => ({
                ...prev,
                lastActivityCall: {
                    timestamp: new Date().toLocaleTimeString(),
                    duration: elapsed,
                    status: `${response.status} ${response.statusText}`
                },
                networkStatus: 'Connected'
            }));

            if (response.status === 401) {
                localStorage.removeItem('access');
                localStorage.removeItem('refresh');
                navigate('/login');
                return;
            }

            // Handle 404 as "no data available" rather than an error
            if (response.status === 404) {
                setActivityData([]);
                setActivitySummary(null);
                // If we get 404, also refresh user info to check connection status
                await refreshUserInfo();
                return;
            }

            // Handle 400 as "no data available" rather than an error (common when user has no activity data)
            if (response.status === 400) {
                // Instead of setting an error, just clear the data and refresh user info
                setActivityData([]);
                setActivitySummary(null);
                await refreshUserInfo();
                return;
            }

            if (!response.ok) {
                const result = await safeJsonParse(response, 'fetching activity data (error)');
                throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await safeJsonParse<any>(response, 'fetching activity data');
            if (!result.success) {
                // If it's an HTML response (CloudFront routing issue), treat as "no data"
                // The empty state UI will show the connection prompt
                if (result.shouldRetry) {
                    setActivityData([]);
                    setActivitySummary(null);
                    await refreshUserInfo();
                    return;
                }
                throw new Error(result.error || 'Failed to parse activity data');
            }

            const data = result.data;

            // Handle API response structure that has 'data' and 'summary' properties
            const activityArray = Array.isArray(data) ? data : (data.data || []);
            const summary = data.summary || null;

            // Process and validate the activity data
            const processedActivityArray = activityArray.map((point: any) => {
                // Ensure we have valid data before processing
                if (!point || typeof point !== 'object') {
                    return null;
                }

                // Parse numeric values and ensure they're numbers
                const strain = typeof point.strain === 'number' ? point.strain :
                    typeof point.strain === 'string' ? parseFloat(point.strain) : 0;

                const kilojoule = typeof point.kilojoule === 'number' ? point.kilojoule :
                    typeof point.kilojoule === 'string' ? parseFloat(point.kilojoule) : 0;

                const avgHeartRate = typeof point.average_heart_rate === 'number' ? point.average_heart_rate :
                    typeof point.average_heart_rate === 'string' ? parseFloat(point.average_heart_rate) : 0;

                const maxHeartRate = typeof point.max_heart_rate === 'number' ? point.max_heart_rate :
                    typeof point.max_heart_rate === 'string' ? parseFloat(point.max_heart_rate) : 0;

                // Handle duration - can come from duration_hours or calculated from zone durations
                let durationMinutes = 0;
                if (point.duration_hours && typeof point.duration_hours === 'number') {
                    durationMinutes = Math.round(point.duration_hours * 60);
                } else {
                    // Calculate duration in minutes from zone durations (fallback)
                    const zoneDuration = point.zone_duration || {};
                    const totalMilliseconds = Object.values(zoneDuration).reduce((sum: number, value: unknown) =>
                        sum + (typeof value === 'number' ? value : 0), 0);
                    durationMinutes = Math.round(totalMilliseconds / (1000 * 60));
                }

                // Handle calories - can come from calories_burned field or calculated from kilojoule
                const caloriesBurned = point.calories_burned || (kilojoule * 0.239);

                return {
                    date: point.date || new Date().toISOString().split('T')[0],
                    activity_type: point.activity_type || 'Daily Activity',
                    duration_minutes: durationMinutes,
                    strain: strain,
                    kilojoule: kilojoule,
                    average_heart_rate: avgHeartRate,
                    max_heart_rate: maxHeartRate,
                    distance_meter: 0, // Not provided by cycles endpoint
                    altitude_gain_meter: 0, // Not provided by cycles endpoint
                    percent_recorded: 100, // Assume full recording for daily cycles
                    zone_duration: {
                        zone_zero_milli: 0,
                        zone_one_milli: 0,
                        zone_two_milli: 0,
                        zone_three_milli: 0,
                        zone_four_milli: 0,
                        zone_five_milli: 0
                    },
                    created_at: point.created_at || new Date().toISOString(),
                    calories_burned: Math.round(caloriesBurned),
                    source: point.source || 'daily_cycle'
                };
            }).filter((point: ActivityDataPoint | null): point is ActivityDataPoint => point !== null);

            // Sort the data by date (newest first to match backend sorting)
            const sortedActivityArray = processedActivityArray.sort((a: ActivityDataPoint, b: ActivityDataPoint) =>
                new Date(b.date).getTime() - new Date(a.date).getTime()
            );

            setActivityData(sortedActivityArray);
            setActivitySummary(summary);

        } catch (err) {
            const elapsed = Date.now() - startTime;
            console.error(`Dashboard: Error after ${elapsed}ms:`, err);

            // Update debug info with error
            setDebugInfo(prev => ({
                ...prev,
                lastActivityCall: {
                    timestamp: new Date().toLocaleTimeString(),
                    duration: elapsed,
                    status: `ERROR: ${err instanceof Error ? err.message : 'Unknown error'}`
                },
                networkStatus: 'Error'
            }));

            setError(err instanceof Error ? err.message : 'An error occurred while fetching activity data');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetch Garmin/Terra data from API
     */
    const fetchTerraData = async (providers?: string[]): Promise<void> => {
        const startTime = Date.now();

        try {
            const token = localStorage.getItem('access');
            if (!token) {
                return;
            }

            const providersParam = providers && providers.length > 0 ? `&providers=${providers.join(',')}` : '';
            const response = await fetch(`/api/wearables/garmin/?days=${days}${providersParam}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const elapsed = Date.now() - startTime;

            // Update debug info
            setDebugInfo(prev => ({
                ...prev,
                lastGarminCall: {
                    timestamp: new Date().toLocaleTimeString(),
                    duration: elapsed,
                    status: `${response.status} ${response.statusText}`
                },
                networkStatus: 'Connected'
            }));

            if (response.status === 401) {
                return;
            }

            if (!response.ok) {
                setTerraData(null);
                return;
            }

            const result = await safeJsonParse<TerraData>(response, 'fetching Terra data');
            if (!result.success) {
                console.error('Dashboard: Failed to parse Garmin data:', result.error);
                setTerraData(null);
                return;
            }

            const terraData = result.data;

            // Normalize data structure - ensure arrays exist even if empty
            const normalizedTerraData: TerraData | null = terraData ? {
                distance_data: Array.isArray(terraData.distance_data) ? terraData.distance_data : [],
                rhr_data: Array.isArray(terraData.rhr_data) ? terraData.rhr_data : [],
                hr_zone_data: Array.isArray(terraData.hr_zone_data) ? terraData.hr_zone_data : [],
                weekly_data: Array.isArray(terraData.weekly_data) ? terraData.weekly_data : [],
                sleep_efficiency_data: Array.isArray(terraData.sleep_efficiency_data) ? terraData.sleep_efficiency_data : [],
                sleep_stages_data: Array.isArray(terraData.sleep_stages_data) ? terraData.sleep_stages_data : [],
                respiratory_rate_data: Array.isArray(terraData.respiratory_rate_data) ? terraData.respiratory_rate_data : [],
                body_temp_data: Array.isArray(terraData.body_temp_data) ? terraData.body_temp_data : [],
                stats: terraData.stats || {
                    average_distance_miles: 0,
                    total_distance_miles: 0,
                    average_rhr: 0,
                    latest_rhr: 0,
                    baseline_rhr: null,
                    distance_data_points: 0,
                    rhr_data_points: 0,
                    average_sleep_efficiency: 0,
                    sleep_efficiency_data_points: 0,
                },
                terra_connected: terraData.terra_connected || false,
            } : null;

            setTerraData(normalizedTerraData);

        } catch (err) {
            const elapsed = Date.now() - startTime;
            console.error(`Dashboard: Garmin fetch error after ${elapsed}ms:`, err);

            setDebugInfo(prev => ({
                ...prev,
                lastGarminCall: {
                    timestamp: new Date().toLocaleTimeString(),
                    duration: elapsed,
                    status: `ERROR: ${err instanceof Error ? err.message : 'Unknown error'}`
                }
            }));

            setTerraData(null);
        }
    };

    /**
     * Refresh user info from backend to get updated connection status
     */
    const refreshUserInfo = async (): Promise<void> => {
        const startTime = Date.now();

        try {
            const token = localStorage.getItem('access');
            if (!token) {
                return;
            }

            const response = await fetch('/api/users/me/', {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const elapsed = Date.now() - startTime;

            // Update debug info
            setDebugInfo(prev => ({
                ...prev,
                lastUserCall: {
                    timestamp: new Date().toLocaleTimeString(),
                    duration: elapsed,
                    status: `${response.status} ${response.statusText}`
                }
            }));

            if (response.ok) {
                const result = await safeJsonParse(response, 'refreshing user info');
                if (result.success) {
                    // Update localStorage and state
                    localStorage.setItem('user_info', JSON.stringify(result.data));
                    setUserInfo(result.data);
                }
            }
        } catch (err) {
            const elapsed = Date.now() - startTime;
            console.error(`Dashboard: refreshUserInfo error after ${elapsed}ms:`, err);

            // Update debug info with error
            setDebugInfo(prev => ({
                ...prev,
                lastUserCall: {
                    timestamp: new Date().toLocaleTimeString(),
                    duration: elapsed,
                    status: `ERROR: ${err instanceof Error ? err.message : 'Unknown error'}`
                }
            }));
        }
    };

    /**
     * Trigger Celery data sync task for background data fetching from WHOOP API.
     * This queues an asynchronous task that will fetch latest data from WHOOP.
     * After triggering, wait a few seconds then refresh local data to see updates.
     */
    const triggerDataSync = async (): Promise<void> => {
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('access');
            if (!token) {
                navigate('/login');
                return;
            }

            const response = await fetch('/api/wearables/sync/', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            const result = await safeJsonParse(response, 'triggering data sync');

            if (result.success) {
                // Wait 8 seconds for Celery task to complete, then fetch fresh data
                setTimeout(async () => {
                    await Promise.all([
                        fetchHRVData(),
                        fetchSleepData(),
                        fetchActivityData(),
                        refreshUserInfo()
                    ]);
                }, 8000);
            } else {
                console.error('Dashboard: Error triggering sync:', result.error);
                setError(result.error || 'Failed to trigger data sync');
                setLoading(false);
            }
        } catch (err) {
            console.error('Dashboard: Error triggering data sync:', err);
            setError('Network error while triggering sync');
            setLoading(false);
        }
    };

    /**
     * Test API connectivity
     */
    const testConnectivity = async (): Promise<void> => {
        try {
            const startTime = Date.now();
            const response = await fetch('/api/health/', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            const elapsed = Date.now() - startTime;

            setDebugInfo(prev => ({
                ...prev,
                networkStatus: response.ok ? `Connected (${elapsed}ms)` : `Error ${response.status}`
            }));

        } catch (err) {
            console.error('Dashboard: Connectivity test failed:', err);
            setDebugInfo(prev => ({
                ...prev,
                networkStatus: 'Failed - No connection'
            }));
        }
    };

    /**
     * Handle user logout
     */
    const handleLogout = (): void => {
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        localStorage.removeItem('user_info');
        navigate('/login');
    };

    /**
     * Get navigation options based on user role
     */
    const getNavigationOptions = () => {
        if (userInfo?.role === 'coach') {
            return [
                { label: 'Personal Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
                { label: 'Team Dashboard', path: '/coach-dashboard', icon: 'Users' },
                { label: 'Team Info', path: '/team-information', icon: 'Info' },
                { label: 'Invite a New User', path: '#invite', icon: 'UserPlus', onClick: () => openInviteUserModal() },
            ];
        }

        const options = [
            { label: 'Personal Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
            { label: 'Connect Whoop', path: '/connect-whoop', icon: 'Watch' }
        ];

        if (userInfo?.role === 'admin') {
            options.push({ label: 'Admin Dashboard', path: '/admin-dashboard', icon: 'Settings' });
        }

        if (userInfo?.role === 'admin' || userInfo?.role === 'coach') {
            options.push({ label: 'Invite a New User', path: '#invite', icon: 'UserPlus', onClick: () => openInviteUserModal() });
        }

        return options;
    };

    /**
     * Generate chart data for HRV visualization
     */
    const generateChartData = () => {
        if (!hrvData || hrvData.length === 0) {
            return {
                labels: [],
                datasets: []
            };
        }

        // Use consistent date range for all charts based on selected days
        const dateLabels = generateDateRange(days);

        // Format labels for display
        const labels = dateLabels.map(date => {
            const displayDate = new Date(date + 'T12:00:00');
            return displayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        });

        // Map HRV data to full date range, using null for missing dates
        // HRV comes from recovery data - no date shift needed, already correct day
        const data = dateLabels.map(date => {
            const dataPoint = hrvData.find(point => point.date === date);
            return dataPoint ? dataPoint.hrv_value : null;
        });

        const chartData = {
            labels: labels,
            datasets: [
                {
                    label: 'HRV',
                    data: data,
                    type: 'line' as const,
                    backgroundColor: isDark ? 'rgba(244, 114, 182, 0.1)' : 'rgba(236, 72, 153, 0.1)',
                    borderColor: isDark ? '#F472B6' : '#EC4899',
                    borderWidth: 2,
                    pointRadius: 0,
                    fill: true,
                    tension: 0.4,
                    spanGaps: true
                }
            ]
        };
        return chartData;
    };

    /**
     * Chart configuration options
     */
    const getChartOptions = () => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                mode: 'index' as const,
                intersect: false,
                backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
                titleColor: isDark ? '#F3F4F6' : '#1F2937',
                bodyColor: isDark ? '#F3F4F6' : '#1F2937',
                borderColor: isDark ? '#374151' : '#E5E7EB',
                borderWidth: 1,
                padding: 12,
                titleFont: { weight: 'bold' as const, size: 14 },
                bodyFont: { size: 13 },
                displayColors: true,
                callbacks: {
                    label: function (context: any) {
                        const label = context.dataset.label || 'HRV';
                        const value = context.parsed.y.toFixed(1);
                        return `${label}: ${value} ms`;
                    },
                    labelTextColor: (context: any) => {
                        return context.dataset.borderColor || (isDark ? '#F3F4F6' : '#1F2937');
                    },
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    color: isDark ? '#374151' : '#F3F4F6'
                },
                ticks: {
                    color: isDark ? '#9CA3AF' : '#6B7280',
                    font: {
                        size: 12
                    }
                }
            },
            y: {
                grid: {
                    display: true,
                    color: isDark ? '#374151' : '#F3F4F6'
                },
                ticks: {
                    color: isDark ? '#9CA3AF' : '#6B7280',
                    font: {
                        size: 12
                    }
                }
            }
        }
    });

    /**
     * Calculate HRV statistics
     */
    const calculateStats = () => {
        if (!hrvData || hrvData.length === 0) return null;

        // Filter to only include data points within the visible chart range
        const visibleDates = new Set(generateDateRange(days));
        const sortedData = [...hrvData]
            .filter(point => visibleDates.has(point.date))
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        // Extract HRV values - handle different possible data structures
        const values = sortedData
            .map(point => {
                // Handle different possible property names from API
                return point.hrv_rmssd || point.hrv_value || point.hrv || point.value;
            })
            .filter(val => val !== undefined && val !== null && !isNaN(val))
            .map(val => Number(val));

        if (values.length === 0) return null;

        const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
        const max = Math.max(...values);
        const min = Math.min(...values);
        const latest = values[values.length - 1]; // Most recent value (rightmost)
        const trend = values.length > 1 ? latest - values[values.length - 2] : 0; // Compare with previous value

        return {
            avg: isNaN(avg) ? 0 : avg,
            max: isNaN(max) ? 0 : max,
            min: isNaN(min) ? 0 : min,
            latest: isNaN(latest) ? 0 : latest,
            trend: isNaN(trend) ? 0 : trend,
            count: values.length
        };
    };

    const stats = calculateStats();

    const commands = useMemo(() => generateCommands({
        navigate,
        openCreateUserModal,
        openInviteUserModal,
        openCreateTeamModal,
        openCreateOrgModal,
        setDays,
        fetchHRVData,
        fetchSleepData,
        fetchActivityData,
        refreshUserInfo,
        triggerDataSync,
        setShowDebugPanel: () => setShowDebugPanel(prev => !prev)
    }), [navigate, openCreateUserModal, openInviteUserModal, openCreateTeamModal, openCreateOrgModal]);

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen cgm-bg-primary">
                <div className="flex items-center justify-center min-h-screen">
                    <div className="text-center space-y-8">
                        {/* Pulsing Wave Loading Dots */}
                        <div className="relative flex items-center justify-center">
                            <div className="flex space-x-2">
                                <div className={`w-3 h-3 ${isDark ? 'bg-white' : 'bg-black'} rounded-full loading-dot-wave`}
                                    style={{ animationDelay: '0ms' }}></div>
                                <div className={`w-3 h-3 ${isDark ? 'bg-white' : 'bg-black'} rounded-full loading-dot-wave`}
                                    style={{ animationDelay: '200ms' }}></div>
                                <div className={`w-3 h-3 ${isDark ? 'bg-white' : 'bg-black'} rounded-full loading-dot-wave`}
                                    style={{ animationDelay: '400ms' }}></div>
                            </div>
                        </div>

                        {/* Loading text */}
                        <div className="space-y-3">
                            <h3 className="text-3xl font-bold theme-text-primary">Loading Dashboard</h3>
                            <p className="text-lg theme-text-secondary">Gathering your health insights...</p>
                        </div>
                    </div>
                </div>

                {/* Wave Animation Styles */}
                <style>{`
                    .loading-dot-wave {
                        animation: wave-pulse 1.4s ease-in-out infinite;
                    }

                    @keyframes wave-pulse {
                        0%, 60%, 100% {
                            transform: scale(1) translateY(0);
                            opacity: 0.7;
                        }
                        30% {
                            transform: scale(1.3) translateY(-8px);
                            opacity: 1;
                        }
                    }
                `}</style>
            </div>
        );
    }

    const isDataEmpty = !hrvData || hrvData.length === 0;

    return (
        <div className="min-h-screen cgm-bg-primary">
            {/* Navigation */}
            <Header
                userInfo={userInfo}
                isDark={isDark}
                commands={commands}
            />

            {/* Main Content */}
            <DashboardLayout>
                {/* Athlete Status: Readiness + Injury Risk */}
                <OverlayExportWrapper metricName="readiness" showStyleToggle={false} className="mb-8">
                    <AthleteStatusCard isDark={isDark} />
                </OverlayExportWrapper>

                {/* Data Source Filter */}
                <div className="mb-8 transition-all duration-300">
                    <DataSourceFilter
                        selectedSources={selectedDataSources}
                        connectedSources={connectedSources}
                        onSourceChange={(sources: string[]) => {
                            setSelectedDataSources(sources);
                            // Terra refetch handled by useEffect on selectedDataSources
                        }}
                    />
                </div>

                {/* Chart Category Filter */}
                <ChartCategoryFilter
                    categories={PERSONAL_CHART_CATEGORIES}
                    selected={chartCategory}
                    onChange={setChartCategory}
                />

                {/* Combined Data Graphs (WHOOP + Garmin) - Display at top */}
                {/* Sleep Efficiency Chart - supports both Garmin and WHOOP */}
                {showCategory('sleep') && (
                    (TERRA_PROVIDER_IDS.some(id => selectedDataSources.includes(id)) && terraData?.sleep_efficiency_data && terraData.sleep_efficiency_data.length > 0) ||
                    (selectedDataSources.includes('whoop') && sleepData && sleepData.length > 0)
                ) && (
                    <OverlayExportWrapper metricName="sleep_efficiency" className="mb-8">
                    <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-2xl font-bold theme-text-primary">Sleep Efficiency</h3>
                                <p className="theme-text-secondary">Daily sleep efficiency from connected devices</p>
                            </div>
                            {(() => {
                                // Calculate combined average
                                const allEfficiencies: number[] = [];
                                if (TERRA_PROVIDER_IDS.some(id => selectedDataSources.includes(id)) && terraData?.sleep_efficiency_data) {
                                    terraData.sleep_efficiency_data.forEach(d => {
                                        // Normalize: if value < 2, treat as decimal (0-1) and convert to percentage
                                        const efficiency = d.efficiency < 2 ? d.efficiency * 100 : d.efficiency;
                                        allEfficiencies.push(efficiency);
                                    });
                                }
                                if (selectedDataSources.includes('whoop') && sleepData) {
                                    sleepData.forEach(d => allEfficiencies.push(d.sleep_efficiency_percentage));
                                }
                                const avgEfficiency = allEfficiencies.length > 0
                                    ? allEfficiencies.reduce((a, b) => a + b, 0) / allEfficiencies.length
                                    : 0;

                                return avgEfficiency > 0 && (
                                    <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl ${
                                        avgEfficiency >= 90
                                            ? `${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`
                                            : avgEfficiency >= 80
                                                ? `${isDark ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800'}`
                                                : `${isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'}`
                                    }`}>
                                        <Moon className="w-4 h-4" />
                                        <span className="font-semibold">
                                            {avgEfficiency.toFixed(0)}% avg
                                        </span>
                                    </div>
                                );
                            })()}
                        </div>
                        <div className="flex flex-col h-96 mb-6">
                            <Line
                                data={(() => {
                                    // Use consistent date range for all charts based on selected days
                                    const dateLabels = generateDateRange(days);

                                    // Format labels for display - use simple, clear date format
                                    // Use noon local time to avoid timezone issues (UTC midnight can show as previous day in western timezones)
                                    const labels = dateLabels.map(date => {
                                        const displayDate = new Date(date + 'T12:00:00');
                                        return displayDate.toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric'
                                        });
                                    });

                                    // Merge data from both sources by date - average when both available
                                    const dateMap = new Map<string, { garmin?: number; whoop?: number }>();

                                    if (TERRA_PROVIDER_IDS.some(id => selectedDataSources.includes(id)) && terraData?.sleep_efficiency_data) {
                                        terraData.sleep_efficiency_data.forEach(point => {
                                            const existing = dateMap.get(point.date) || {};
                                            // Normalize: if value < 2, treat as decimal (0-1) and convert to percentage
                                            // Real sleep efficiency is typically 70-95%, so values < 2 indicate decimal format
                                            const efficiency = point.efficiency < 2
                                                ? point.efficiency * 100
                                                : point.efficiency;
                                            dateMap.set(point.date, { ...existing, garmin: efficiency });
                                        });
                                    }

                                    if (selectedDataSources.includes('whoop') && sleepData) {
                                        sleepData.forEach(point => {
                                            const existing = dateMap.get(point.date) || {};
                                            dateMap.set(point.date, { ...existing, whoop: point.sleep_efficiency_percentage });
                                        });
                                    }

                                    const datasets: any[] = [];
                                    const hasGarmin = TERRA_PROVIDER_IDS.some(id => selectedDataSources.includes(id)) && terraData?.sleep_efficiency_data && terraData.sleep_efficiency_data.length > 0;
                                    const hasWhoop = selectedDataSources.includes('whoop') && sleepData && sleepData.length > 0;

                                    // Single averaged dataset - average Garmin and WHOOP when both available
                                    // Map data to full date range, using null for missing dates
                                    const averagedData = dateLabels.map(date => {
                                        // Sleep data is stored under the previous day's date (night it started)
                                        // Shift forward to match the day you wake up
                                        const garminPoint = terraData?.sleep_efficiency_data?.find(point =>
                                            point.date && shiftDateForward(point.date) === date
                                        );
                                        // WHOOP OAuth dates are already wake-up dates, no shift needed
                                        const whoopPoint = sleepData?.find(point =>
                                            point.date === date
                                        );

                                        const values: number[] = [];
                                        if (garminPoint && garminPoint.efficiency != null) {
                                            const efficiency = garminPoint.efficiency < 2
                                                ? garminPoint.efficiency * 100
                                                : garminPoint.efficiency;
                                            values.push(efficiency);
                                        }
                                        if (whoopPoint) {
                                            values.push(whoopPoint.sleep_efficiency_percentage);
                                        }
                                        if (values.length === 0) return null;
                                        return values.reduce((a, b) => a + b, 0) / values.length;
                                    });

                                    datasets.push({
                                        label: 'Sleep Efficiency',
                                        data: averagedData,
                                        borderColor: isDark ? '#8B5CF6' : '#7C3AED',
                                        backgroundColor: isDark ? 'rgba(139, 92, 246, 0.2)' : 'rgba(124, 58, 237, 0.2)',
                                        borderWidth: 3,
                                        pointBackgroundColor: (context: any) => {
                                            const value = context.raw as number;
                                            if (value === null) return 'transparent';
                                            if (value >= 90) return isDark ? '#10B981' : '#059669';
                                            if (value >= 80) return isDark ? '#F59E0B' : '#D97706';
                                            return isDark ? '#EF4444' : '#DC2626';
                                        },
                                        pointBorderColor: '#ffffff',
                                        pointBorderWidth: 2,
                                        pointRadius: 5,
                                        pointHoverRadius: 7,
                                        fill: true,
                                        tension: 0.4,
                                        spanGaps: true,
                                    });

                                    // Threshold lines
                                    datasets.push({
                                        label: 'Optimal (90%)',
                                        data: dateLabels.map(() => 90),
                                        borderColor: isDark ? '#10B981' : '#059669',
                                        backgroundColor: 'transparent',
                                        borderWidth: 1,
                                        borderDash: [5, 5],
                                        pointRadius: 0,
                                        fill: false,
                                    });
                                    datasets.push({
                                        label: 'Adequate (80%)',
                                        data: dateLabels.map(() => 80),
                                        borderColor: isDark ? '#F59E0B' : '#D97706',
                                        backgroundColor: 'transparent',
                                        borderWidth: 1,
                                        borderDash: [5, 5],
                                        pointRadius: 0,
                                        fill: false,
                                    });

                                    return { labels, datasets };
                                })()}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: {
                                            display: true,
                                            position: 'top' as const,
                                            labels: {
                                                color: isDark ? '#9CA3AF' : '#6B7280',
                                                usePointStyle: true,
                                            }
                                        },
                                        tooltip: {
                                            backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
                                            titleColor: isDark ? '#F9FAFB' : '#111827',
                                            bodyColor: isDark ? '#D1D5DB' : '#374151',
                                            borderColor: isDark ? '#374151' : '#E5E7EB',
                                            borderWidth: 1,
                                            cornerRadius: 12,
                                            displayColors: true,
                                            titleFont: { weight: 'bold' },
                                            callbacks: {
                                                label: (context: any) => {
                                                    const label = context.dataset.label;
                                                    if (label?.includes('Optimal') || label?.includes('Adequate')) {
                                                        return '';
                                                    }
                                                    const value = context.parsed.y;
                                                    if (value === null) return '';
                                                    const quality = value >= 90 ? 'Excellent' : value >= 80 ? 'Good' : 'Needs Improvement';
                                                    return ` ${label}: ${value.toFixed(1)}% - ${quality}`;
                                                },
                                            },
                                        },
                                    },
                                    scales: {
                                        x: {
                                            display: true,
                                            grid: {
                                                color: isDark ? '#374151' : '#F3F4F6',
                                            },
                                            ticks: {
                                                color: isDark ? '#9CA3AF' : '#6B7280',
                                                font: { size: 12 },
                                            },
                                            border: {
                                                display: false,
                                            },
                                        },
                                        y: {
                                            display: true,
                                            min: 50,
                                            max: 100,
                                            grid: {
                                                color: isDark ? '#374151' : '#F3F4F6',
                                            },
                                            ticks: {
                                                color: isDark ? '#9CA3AF' : '#6B7280',
                                                font: { size: 12 },
                                                callback: (value: any) => `${value}%`,
                                            },
                                            border: {
                                                display: false,
                                            },
                                            title: {
                                                display: true,
                                                text: 'Sleep Efficiency (%)',
                                                color: isDark ? '#9CA3AF' : '#6B7280',
                                                font: { size: 12 },
                                            },
                                        },
                                    },
                                }}
                            />
                            {/* Show attribution based on available data sources */}
                            <DynamicAttribution sources={[
                                ...(TERRA_PROVIDER_IDS.some(id => selectedDataSources.includes(id)) && terraData?.sleep_efficiency_data?.length ? selectedDataSources.filter(s => TERRA_PROVIDER_IDS.includes(s)) : []),
                                ...(selectedDataSources.includes('whoop') && sleepData?.length ? ['whoop'] : []),
                            ]} />
                        </div>
                    </div>
                    </OverlayExportWrapper>
                )}

                {/* Combined Sleep Stages Chart - shows when both WHOOP and Garmin are selected and have data */}
                {showCategory('sleep') && selectedDataSources.includes('whoop') && TERRA_PROVIDER_IDS.some(id => selectedDataSources.includes(id)) &&
                 sleepData && sleepData.length > 0 &&
                 terraData?.sleep_stages_data && terraData.sleep_stages_data.length > 0 && (
                    <OverlayExportWrapper metricName="sleep_stages_combined" className="mb-8">
                    <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-2xl font-bold theme-text-primary">Sleep Stages</h3>
                                <p className="theme-text-secondary">Combined breakdown of your sleep stages from all sources</p>
                            </div>
                        </div>
                        <div className="flex flex-col h-96 mb-6">
                            <Line
                                data={(() => {
                                    // Use consistent date range for all charts based on selected days
                                    const dateLabels = generateDateRange(days);

                                    // Format labels for display - use simple, clear date format
                                    const labels = dateLabels.map(date => {
                                        const displayDate = new Date(date + 'T12:00:00');
                                        return displayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                    });

                                    // Build a combined date map with averaged sleep stage data
                                    const dateMap = new Map<string, { light: number[], deep: number[], rem: number[] }>();

                                    // Process WHOOP data (time is in milliseconds, convert to hours)
                                    // WHOOP OAuth dates are already wake-up dates, no shift needed
                                    [...sleepData]
                                        .filter(point => point.date)
                                        .forEach(point => {
                                            const dateKey = point.date;

                                            const existing = dateMap.get(dateKey) || { light: [], deep: [], rem: [] };
                                            if (point.total_light_sleep_time_milli) {
                                                existing.light.push((point.total_light_sleep_time_milli || 0) / (1000 * 60 * 60));
                                            }
                                            if (point.total_slow_wave_sleep_time_milli) {
                                                existing.deep.push((point.total_slow_wave_sleep_time_milli || 0) / (1000 * 60 * 60));
                                            }
                                            if (point.total_rem_sleep_time_milli) {
                                                existing.rem.push((point.total_rem_sleep_time_milli || 0) / (1000 * 60 * 60));
                                            }
                                            dateMap.set(dateKey, existing);
                                        });

                                    // Process Garmin data (time is in minutes, convert to hours)
                                    // Garmin DB dates are sleep start dates, shift forward to wake-up date
                                    [...(terraData?.sleep_stages_data || [])]
                                        .forEach(point => {
                                            const dateKey = point.date ? shiftDateForward(point.date) : point.date;
                                            const existing = dateMap.get(dateKey) || { light: [], deep: [], rem: [] };
                                            if (point.light_sleep_minutes) {
                                                existing.light.push((point.light_sleep_minutes || 0) / 60);
                                            }
                                            if (point.deep_sleep_minutes) {
                                                existing.deep.push((point.deep_sleep_minutes || 0) / 60);
                                            }
                                            if (point.rem_sleep_minutes) {
                                                existing.rem.push((point.rem_sleep_minutes || 0) / 60);
                                            }
                                            dateMap.set(dateKey, existing);
                                        });

                                    const avgOrNull = (arr: number[]) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : null;

                                    return {
                                        labels,
                                        datasets: [
                                            {
                                                label: 'Light Sleep',
                                                data: dateLabels.map(d => avgOrNull(dateMap.get(d)?.light || [])),
                                                borderColor: isDark ? '#60A5FA' : '#3B82F6',
                                                backgroundColor: isDark ? 'rgba(96, 165, 250, 0.6)' : 'rgba(59, 130, 246, 0.6)',
                                                borderWidth: 0,
                                                fill: true,
                                                tension: 0.4,
                                                spanGaps: false, // Don't connect lines across missing data
                                            },
                                            {
                                                label: 'Deep Sleep',
                                                data: dateLabels.map(d => avgOrNull(dateMap.get(d)?.deep || [])),
                                                borderColor: isDark ? '#34D399' : '#10B981',
                                                backgroundColor: isDark ? 'rgba(52, 211, 153, 0.6)' : 'rgba(16, 185, 129, 0.6)',
                                                borderWidth: 0,
                                                fill: true,
                                                tension: 0.4,
                                                spanGaps: false, // Don't connect lines across missing data
                                            },
                                            {
                                                label: 'REM Sleep',
                                                data: dateLabels.map(d => avgOrNull(dateMap.get(d)?.rem || [])),
                                                borderColor: isDark ? '#F472B6' : '#EC4899',
                                                backgroundColor: isDark ? 'rgba(244, 114, 182, 0.6)' : 'rgba(236, 72, 153, 0.6)',
                                                borderWidth: 0,
                                                fill: true,
                                                tension: 0.4,
                                                spanGaps: false, // Don't connect lines across missing data
                                            }
                                        ]
                                    };
                                })()}
                                options={{
                                    ...getChartOptions(),
                                    scales: {
                                        ...getChartOptions().scales,
                                        y: {
                                            ...getChartOptions().scales.y,
                                            stacked: true,
                                            ticks: {
                                                ...getChartOptions().scales.y.ticks,
                                                callback: (value: any) => `${value} hrs`,
                                            },
                                        },
                                        x: {
                                            ...getChartOptions().scales.x,
                                            stacked: true,
                                        }
                                    },
                                    plugins: {
                                        ...getChartOptions().plugins,
                                        tooltip: {
                                            ...getChartOptions().plugins.tooltip,
                                            callbacks: {
                                                title: (context: any) => {
                                                    return `${context[0].label}`;
                                                },
                                                label: (context: any) => {
                                                    // Check if all datasets have null values for this date
                                                    const dataIndex = context.dataIndex;
                                                    const allNull = context.chart.data.datasets.every((ds: any) => 
                                                        ds.data[dataIndex] === null || ds.data[dataIndex] === undefined
                                                    );
                                                    
                                                    if (allNull) {
                                                        return 'No sleep stages data available for this date';
                                                    }
                                                    
                                                    const value = context.parsed.y;
                                                    if (value === null || value === undefined) {
                                                        return ` ${context.dataset.label}: No data`;
                                                    }
                                                    return ` ${context.dataset.label}: ${value.toFixed(1)} hrs`;
                                                },
                                                footer: (context: any) => {
                                                    // Check if all datasets have null values for this date
                                                    const dataIndex = context[0].dataIndex;
                                                    const allNull = context[0].chart.data.datasets.every((ds: any) => 
                                                        ds.data[dataIndex] === null || ds.data[dataIndex] === undefined
                                                    );
                                                    
                                                    if (allNull) {
                                                        return '\nℹ️ Data may be syncing or unavailable';
                                                    }
                                                    return '';
                                                },
                                            },
                                        },
                                    },
                                }}
                            />
                            <DynamicAttribution sources={[...selectedDataSources.filter(s => TERRA_PROVIDER_IDS.includes(s)), 'whoop']} />
                        </div>
                    </div>
                    </OverlayExportWrapper>
                )}

                {/* HRV Analysis Controls Section */}
                <div className="mb-8">
                    <div className={`cgm-card rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                            <div className="space-y-3">
                                <h2 className="text-2xl font-bold theme-text-primary">HRV Analysis</h2>
                                <p className="theme-text-secondary">Monitor your heart rate variability trends and recovery insights</p>
                            </div>
                            <div className="lg:min-w-[200px]">
                                <label className="block text-sm font-medium theme-text-primary mb-2">Analysis Period</label>
                                <select
                                    value={days}
                                    onChange={(e) => setDays(parseInt(e.target.value))}
                                    className={`w-full px-4 py-3 rounded-xl border ${isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'} theme-text-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-lg font-medium`}
                                >
                                    <option value={7}>Last 7 days</option>
                                    <option value={14}>Last 2 weeks</option>
                                    <option value={30}>Last 30 days</option>
                                    <option value={60}>Last 60 days</option>
                                    <option value={90}>Last 90 days</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>


                {error && (
                    <div className="mb-8">
                        {error === 'whoop_oauth_required' ? (
                            <div className={`${isDark ? 'bg-orange-900/20' : 'bg-orange-50'} border ${isDark ? 'border-orange-700' : 'border-orange-200'} rounded-2xl p-6`}>
                                <div className="flex items-start space-x-4">
                                    <div className={`w-12 h-12 ${isDark ? 'bg-orange-900/30' : 'bg-orange-100'} rounded-xl flex items-center justify-center flex-shrink-0`}>
                                        <svg className={`w-6 h-6 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className={`text-lg font-semibold ${isDark ? 'text-orange-200' : 'text-orange-800'} mb-2`}>
                                            Whoop OAuth Connection Required
                                        </h3>
                                        <p className={`${isDark ? 'text-orange-300' : 'text-orange-700'} mb-4 leading-relaxed`}>
                                            To access your HRV data, you need to connect your Whoop account using our secure OAuth 2.0 integration.
                                            This ensures your credentials remain secure and gives you control over data access.
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <Link
                                                to="/connect-whoop"
                                                className="inline-flex items-center space-x-2 cgm-button px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                </svg>
                                                <span>Connect Whoop Account</span>
                                            </Link>
                                            <button
                                                onClick={() => {
                                                    setError('');
                                                    triggerDataSync();  // Triggers Celery task for backend data sync
                                                }}
                                                className={`inline-flex items-center space-x-2 ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} px-6 py-3 rounded-xl font-semibold transition-all duration-200`}
                                                disabled={loading}
                                            >
                                                {loading ? (
                                                    <>
                                                        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                                        <span>Syncing...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                        </svg>
                                                        <span>Sync Data</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className={`${isDark ? 'bg-red-900/20' : 'bg-red-50'} border ${isDark ? 'border-red-700' : 'border-red-200'} rounded-2xl p-6`}>
                                <div className="flex items-center space-x-3">
                                    <div className={`w-10 h-10 ${isDark ? 'bg-red-900/30' : 'bg-red-100'} rounded-xl flex items-center justify-center`}>
                                        <svg className={`w-6 h-6 ${isDark ? 'text-red-400' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className={`text-lg font-semibold ${isDark ? 'text-red-200' : 'text-red-800'}`}>Unable to Load Data</h3>
                                        <p className={`${isDark ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {selectedDataSources.includes('whoop') && !isDataEmpty && stats && (
                    <>
                        {/* Stats Grid + HRV Chart — Recovery */}
                        {showCategory('recovery') && <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                            {[
                                {
                                    title: 'Latest HRV',
                                    value: `${(stats?.latest ?? 0).toFixed(1)} ms`,
                                    Icon: HeartPulse
                                },
                                {
                                    title: 'Average',
                                    value: `${(stats?.avg ?? 0).toFixed(1)} ms`,
                                    Icon: BarChart3
                                },
                                {
                                    title: 'Highest',
                                    value: `${(stats?.max ?? 0).toFixed(1)} ms`,
                                    Icon: TrendingUp
                                },
                                {
                                    title: 'Lowest',
                                    value: `${(stats?.min ?? 0).toFixed(1)} ms`,
                                    Icon: TrendingDown
                                },
                                {
                                    title: 'Data Points',
                                    value: `${stats?.count ?? 0}`,
                                    Icon: ClipboardList
                                }
                            ].map((stat, index) => {
                                const iconColors = ['cgm-icon-hrv', 'cgm-icon-data', 'cgm-icon-success', 'cgm-icon-warning', 'cgm-icon-neutral'];
                                return (
                                    <div key={index} className={`cgm-metric-card`}>
                                        <div className="flex items-center gap-3 mb-4">
                                            <stat.Icon className={`w-8 h-8 flex-shrink-0 ${iconColors[index]}`} />
                                            <div className="text-sm cgm-text-secondary font-medium">{stat.title}</div>
                                        </div>
                                        <div className="text-3xl font-bold theme-text-primary">{stat.value}</div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Chart Section */}
                        <OverlayExportWrapper metricName="hrv_trend" className="mb-8">
                        <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h3 className="text-2xl font-bold theme-text-primary">HRV Trend</h3>
                                    <p className="theme-text-secondary">Your heart rate variability over time</p>
                                </div>
                                {(stats?.trend ?? 0) !== 0 && (
                                    <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl ${(stats?.trend ?? 0) > 0
                                        ? `${isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-800'}`
                                        : `${isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'}`
                                        }`}>
                                        {((stats?.trend ?? 0) > 0) ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                        <span className="font-semibold">
                                            {((stats?.trend ?? 0) > 0) ? '+' : ''}{(stats?.trend ?? 0).toFixed(1)} ms
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col h-96 mb-6">
                                <Line data={generateChartData()} options={getChartOptions()} />
                                <DynamicAttribution sources={['whoop']} />
                            </div>
                        </div>
                        </OverlayExportWrapper>

                        </>}

                        {/* Sleep Hours Chart Section */}
                        {showCategory('sleep') && selectedDataSources.includes('whoop') && sleepData && sleepData.length > 0 && (
                            <>
                                <OverlayExportWrapper metricName="sleep_hours" className="mb-8">
                                <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h3 className="text-2xl font-bold theme-text-primary">Sleep Hours</h3>
                                            <p className="theme-text-secondary">Your daily sleep duration</p>
                                        </div>
                                        {sleepSummary && sleepSummary.latest_sleep_hours !== null && (
                                            <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl ${sleepSummary.latest_sleep_hours >= 7
                                                ? `${isDark ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-100 text-emerald-800'}`
                                                : `${isDark ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-800'}`
                                                }`}>
                                                <span>💤</span>
                                                <span className="font-semibold">
                                                    {sleepSummary.latest_sleep_hours.toFixed(1)} hrs
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col h-96 mb-6">
                                        <Line
                                            data={{
                                                labels: [...sleepData]
                                                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                    .map(point => {
                                                        // Use noon local time to avoid timezone issues (UTC midnight shows as previous day in western timezones)
                                                        return new Date(point.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                                    }),
                                                datasets: [{
                                                    label: 'Sleep Hours',
                                                    data: [...sleepData]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(point => {
                                                            // Calculate total sleep time from individual stages
                                                            const totalSleepMs = (
                                                                (point.total_light_sleep_time_milli || 0) +
                                                                (point.total_slow_wave_sleep_time_milli || 0) +
                                                                (point.total_rem_sleep_time_milli || 0)
                                                            );
                                                            return (totalSleepMs / (1000 * 60 * 60)).toFixed(1);
                                                        }),
                                                    borderColor: isDark ? '#60A5FA' : '#3B82F6',
                                                    backgroundColor: isDark ? 'rgba(96, 165, 250, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                                                    borderWidth: 3,
                                                    pointBackgroundColor: isDark ? '#60A5FA' : '#3B82F6',
                                                    pointBorderColor: '#ffffff',
                                                    pointBorderWidth: 2,
                                                    pointRadius: 6,
                                                    pointHoverRadius: 8,
                                                    fill: true,
                                                    tension: 0.4,
                                                }]
                                            }}
                                            options={{
                                                ...getChartOptions(),
                                                scales: {
                                                    ...getChartOptions().scales,
                                                    y: {
                                                        ...getChartOptions().scales.y,
                                                        ticks: {
                                                            ...getChartOptions().scales.y.ticks,
                                                            callback: (value: any) => `${value} hrs`,
                                                        },
                                                    },
                                                },
                                                plugins: {
                                                    ...getChartOptions().plugins,
                                                    tooltip: {
                                                        ...getChartOptions().plugins.tooltip,
                                                        callbacks: {
                                                            title: (context: any) => {
                                                                return `${context[0].label}`;
                                                            },
                                                            label: (context: any) => {
                                                                return ` Sleep: ${context.parsed.y} hrs`;
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        <DynamicAttribution sources={['whoop']} />
                                    </div>
                                </div>
                                </OverlayExportWrapper>

                                {/* Sleep Stages Stacked Area Chart - WHOOP only (hidden when combined chart is shown) */}
                                {!(TERRA_PROVIDER_IDS.some(id => selectedDataSources.includes(id)) && terraData?.sleep_stages_data && terraData.sleep_stages_data.length > 0) && (
                                <OverlayExportWrapper metricName="sleep_stages" className="mb-8">
                                <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h3 className="text-2xl font-bold theme-text-primary">Sleep Stages</h3>
                                            <p className="theme-text-secondary">Breakdown of your sleep stages over time</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col h-96 mb-6">
                                        <Line
                                            data={{
                                                labels: [...sleepData]
                                                    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                    .map(point => {
                                                        // Use noon local time to avoid timezone issues (UTC midnight shows as previous day in western timezones)
                                                        return new Date(point.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                                    }),
                                                datasets: [
                                                    {
                                                        label: 'Light Sleep',
                                                        data: [...sleepData]
                                                            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                            .map(point => (point.total_light_sleep_time_milli || 0) / (1000 * 60 * 60)),
                                                        borderColor: isDark ? '#60A5FA' : '#3B82F6',
                                                        backgroundColor: isDark ? 'rgba(96, 165, 250, 0.6)' : 'rgba(59, 130, 246, 0.6)',
                                                        borderWidth: 0,
                                                        fill: true,
                                                        tension: 0.4,
                                                    },
                                                    {
                                                        label: 'Deep Sleep',
                                                        data: [...sleepData]
                                                            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                            .map(point => (point.total_slow_wave_sleep_time_milli || 0) / (1000 * 60 * 60)),
                                                        borderColor: isDark ? '#34D399' : '#10B981',
                                                        backgroundColor: isDark ? 'rgba(52, 211, 153, 0.6)' : 'rgba(16, 185, 129, 0.6)',
                                                        borderWidth: 0,
                                                        fill: true,
                                                        tension: 0.4,
                                                    },
                                                    {
                                                        label: 'REM Sleep',
                                                        data: [...sleepData]
                                                            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                            .map(point => (point.total_rem_sleep_time_milli || 0) / (1000 * 60 * 60)),
                                                        borderColor: isDark ? '#F472B6' : '#EC4899',
                                                        backgroundColor: isDark ? 'rgba(244, 114, 182, 0.6)' : 'rgba(236, 72, 153, 0.6)',
                                                        borderWidth: 0,
                                                        fill: true,
                                                        tension: 0.4,
                                                    }
                                                ]
                                            }}
                                            options={{
                                                ...getChartOptions(),
                                                scales: {
                                                    ...getChartOptions().scales,
                                                    y: {
                                                        ...getChartOptions().scales.y,
                                                        stacked: true,
                                                        ticks: {
                                                            ...getChartOptions().scales.y.ticks,
                                                            callback: (value: any) => `${value} hrs`,
                                                        },
                                                    },
                                                    x: {
                                                        ...getChartOptions().scales.x,
                                                        stacked: true,
                                                    }
                                                },
                                                plugins: {
                                                    ...getChartOptions().plugins,
                                                    tooltip: {
                                                        ...getChartOptions().plugins.tooltip,
                                                        callbacks: {
                                                            title: (context: any) => {
                                                                return `${context[0].label}`;
                                                            },
                                                            label: (context: any) => {
                                                                return ` ${context.dataset.label}: ${context.parsed.y.toFixed(1)} hrs`;
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        <DynamicAttribution sources={['whoop']} />
                                    </div>
                                </div>
                                </OverlayExportWrapper>
                                )}

                                {/* Sleep Distribution Chart */}
                                <OverlayExportWrapper metricName="sleep_distribution" className="mb-8">
                                <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h3 className="text-2xl font-bold theme-text-primary">Sleep Distribution</h3>
                                            <p className="theme-text-secondary">Distribution of your sleep duration</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col h-96 mb-6">
                                        <Chart type="bar"
                                            data={{
                                                labels: ((): string[] => {
                                                    // Calculate sleep hours for each night
                                                    const sleepHours = [...sleepData]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(point => {
                                                            const totalSleepMs = (
                                                                (point.total_light_sleep_time_milli || 0) +
                                                                (point.total_slow_wave_sleep_time_milli || 0) +
                                                                (point.total_rem_sleep_time_milli || 0)
                                                            );
                                                            return totalSleepMs / (1000 * 60 * 60);
                                                        });

                                                    if (sleepHours.length === 0) return [];

                                                    // Calculate min and max with some padding
                                                    const min = Math.floor(Math.min(...sleepHours) - 0.5);
                                                    const max = Math.ceil(Math.max(...sleepHours) + 0.5);
                                                    const range = max - min;

                                                    // Create bins (aim for about 10-12 bins)
                                                    const binCount = Math.min(Math.max(Math.ceil(range), 8), 12);
                                                    const binSize = range / binCount;

                                                    // Generate labels for each bin
                                                    return Array.from({ length: binCount }, (_, i) => {
                                                        const start = (min + i * binSize).toFixed(1);
                                                        const end = (min + (i + 1) * binSize).toFixed(1);
                                                        return `${start}-${end}`;
                                                    });
                                                })(),
                                                datasets: [
                                                    {
                                                        label: 'Number of Nights',
                                                        data: (() => {
                                                            // Calculate sleep hours for each night
                                                            const sleepHours = [...sleepData]
                                                                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                                .map(point => {
                                                                    const totalSleepMs = (
                                                                        (point.total_light_sleep_time_milli || 0) +
                                                                        (point.total_slow_wave_sleep_time_milli || 0) +
                                                                        (point.total_rem_sleep_time_milli || 0)
                                                                    );
                                                                    return totalSleepMs / (1000 * 60 * 60);
                                                                });

                                                            if (sleepHours.length === 0) return [];

                                                            // Calculate min and max with some padding
                                                            const min = Math.floor(Math.min(...sleepHours) - 0.5);
                                                            const max = Math.ceil(Math.max(...sleepHours) + 0.5);
                                                            const range = max - min;

                                                            // Create bins (aim for about 10-12 bins)
                                                            const binCount = Math.min(Math.max(Math.ceil(range), 8), 12);
                                                            const binSize = range / binCount;

                                                            // Create histogram bins
                                                            const bins = Array(binCount).fill(0);
                                                            sleepHours.forEach(hours => {
                                                                const binIndex = Math.min(Math.max(Math.floor((hours - min) / binSize), 0), binCount - 1);
                                                                bins[binIndex]++;
                                                            });

                                                            return bins;
                                                        })(),
                                                        backgroundColor: isDark ? 'rgba(96, 165, 250, 0.6)' : 'rgba(59, 130, 246, 0.6)',
                                                        borderColor: isDark ? '#60A5FA' : '#3B82F6',
                                                        borderWidth: 1,
                                                        borderRadius: 4,
                                                        yAxisID: 'y',
                                                    },
                                                    {
                                                        label: 'Sleep Pattern',
                                                        data: (() => {
                                                            const sleepHours = [...sleepData]
                                                                .map(point => {
                                                                    const totalSleepMs = (
                                                                        (point.total_light_sleep_time_milli || 0) +
                                                                        (point.total_slow_wave_sleep_time_milli || 0) +
                                                                        (point.total_rem_sleep_time_milli || 0)
                                                                    );
                                                                    return totalSleepMs / (1000 * 60 * 60);
                                                                });

                                                            if (sleepHours.length === 0) return [];

                                                            const min = Math.floor(Math.min(...sleepHours) - 0.5);
                                                            const max = Math.ceil(Math.max(...sleepHours) + 0.5);
                                                            const range = max - min;
                                                            const binCount = Math.min(Math.max(Math.ceil(range), 8), 12);
                                                            const binSize = range / binCount;

                                                            // Silverman's rule of thumb for bandwidth
                                                            const mean = sleepHours.reduce((a, b) => a + b, 0) / sleepHours.length;
                                                            const variance = sleepHours.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / sleepHours.length;
                                                            const stdDev = Math.sqrt(variance);
                                                            const bandwidth = 1.06 * stdDev * Math.pow(sleepHours.length, -0.2);

                                                            return Array.from({ length: binCount }, (_, i) => {
                                                                const x = min + (i + 0.5) * binSize;
                                                                // KDE at x
                                                                const density = sleepHours.reduce((sum, xi) => {
                                                                    const z = (x - xi) / bandwidth;
                                                                    return sum + Math.exp(-0.5 * z * z) / (bandwidth * Math.sqrt(2 * Math.PI));
                                                                }, 0) / sleepHours.length;
                                                                // Scale to match histogram
                                                                return density * sleepHours.length * binSize;
                                                            });
                                                        })(),
                                                        type: 'line' as const,
                                                        borderColor: isDark ? '#F472B6' : '#EC4899',
                                                        borderWidth: 2,
                                                        pointRadius: 0,
                                                        fill: false,
                                                        tension: 0.4,
                                                        yAxisID: 'y',
                                                    }
                                                ]
                                            }}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: {
                                                        display: true,
                                                        position: 'top' as const,
                                                        labels: {
                                                            color: isDark ? '#9CA3AF' : '#6B7280',
                                                            usePointStyle: true,
                                                            pointStyle: 'circle',
                                                        }
                                                    },
                                                    tooltip: {
                                                        backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
                                                        titleColor: isDark ? '#F9FAFB' : '#111827',
                                                        bodyColor: isDark ? '#D1D5DB' : '#374151',
                                                        borderColor: isDark ? '#374151' : '#E5E7EB',
                                                        borderWidth: 1,
                                                        cornerRadius: 12,
                                                        displayColors: false,
                                                        titleFont: { weight: 'bold' },
                                                        callbacks: {
                                                            title: (context: any) => {
                                                                return `Sleep Duration: ${context[0].label} hours`;
                                                            },
                                                            label: (context: any) => {
                                                                if (context.dataset.label === 'Number of Nights') {
                                                                    return ` Nights: ${context.parsed.y}`;
                                                                }
                                                                return ` Expected: ${context.parsed.y.toFixed(1)} nights`;
                                                            },
                                                        },
                                                    },
                                                },
                                                scales: {
                                                    x: {
                                                        display: true,
                                                        grid: {
                                                            color: isDark ? '#374151' : '#F3F4F6',
                                                            display: true
                                                        },
                                                        ticks: {
                                                            color: isDark ? '#9CA3AF' : '#6B7280',
                                                            font: { size: 12 },
                                                        },
                                                        border: {
                                                            display: false,
                                                        },
                                                        title: {
                                                            display: true,
                                                            text: 'Hours of Sleep',
                                                            color: isDark ? '#9CA3AF' : '#6B7280',
                                                            font: { size: 12 },
                                                        },
                                                    },
                                                    y: {
                                                        display: true,
                                                        grid: {
                                                            color: isDark ? '#374151' : '#F3F4F6',
                                                            display: true
                                                        },
                                                        ticks: {
                                                            color: isDark ? '#9CA3AF' : '#6B7280',
                                                            font: { size: 12 },
                                                            stepSize: 1,
                                                        },
                                                        border: {
                                                            display: false,
                                                        },
                                                        title: {
                                                            display: true,
                                                            text: 'Number of Nights',
                                                            color: isDark ? '#9CA3AF' : '#6B7280',
                                                            font: { size: 12 },
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        <DynamicAttribution sources={['whoop']} />
                                    </div>
                                </div>
                                </OverlayExportWrapper>
                            </>
                        )}

                        {/* Activity Strain Chart Section */}
                        {showCategory('activity') && selectedDataSources.includes('whoop') && activityData && activityData.length > 0 && (
                            <OverlayExportWrapper metricName="activity_strain" className="mb-8">
                            <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-2xl font-bold theme-text-primary">Activity Strain</h3>
                                        <p className="theme-text-secondary">Your workout intensity over time</p>
                                    </div>
                                    {activitySummary && activitySummary.latest_strain !== null && (
                                        <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl ${activitySummary.latest_strain >= 10
                                            ? `${isDark ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-800'}`
                                            : `${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'}`
                                            }`}>
                                            <Dumbbell className="w-4 h-4" />
                                            <span className="font-semibold">
                                                {activitySummary.latest_strain.toFixed(1)} strain
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col h-96 mb-6">
                                    <Bar
                                        data={{
                                            labels: [...activityData]
                                                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                .map(point => {
                                                    return new Date(point.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                                                }),
                                            datasets: [
                                                {
                                                    label: 'Strain Score',
                                                    data: [...activityData]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(point => {
                                                            return point.strain || 0;
                                                        }),
                                                    backgroundColor: [...activityData]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(point => {
                                                            const strain = point.strain || 0;
                                                            if (strain >= 15) return isDark ? 'rgba(239, 68, 68, 0.8)' : 'rgba(239, 68, 68, 0.6)';
                                                            if (strain >= 15) return isDark ? '#EF4444' : '#DC2626';
                                                            if (strain >= 10) return isDark ? '#F59E0B' : '#D97706';
                                                            if (strain >= 5) return isDark ? '#3B82F6' : '#2563EB';
                                                            return isDark ? '#9CA3AF' : '#6B7280';
                                                        }),
                                                    borderWidth: 1,
                                                    borderRadius: 4,
                                                    barPercentage: 0.8,
                                                    categoryPercentage: 0.9,
                                                }
                                            ]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {
                                                    display: false,
                                                },
                                                tooltip: {
                                                    backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
                                                    titleColor: isDark ? '#F9FAFB' : '#111827',
                                                    bodyColor: isDark ? '#D1D5DB' : '#374151',
                                                    borderColor: isDark ? '#374151' : '#E5E7EB',
                                                    borderWidth: 1,
                                                    cornerRadius: 12,
                                                    displayColors: false,
                                                    titleFont: { weight: 'bold' },
                                                    callbacks: {
                                                        title: (context: any) => {
                                                            const date = context[0].label;
                                                            const activity = activityData.find(a =>
                                                                new Date(a.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) === date
                                                            );
                                                            return `${date} - ${activity?.activity_type || 'Activity'}`;
                                                        },
                                                        label: (context: any) => {
                                                            const strain = context.parsed.y;
                                                            let intensity = '';
                                                            if (strain >= 15) intensity = 'High';
                                                            else if (strain >= 10) intensity = 'Medium';
                                                            else if (strain >= 5) intensity = 'Low';
                                                            else intensity = 'Very Low';

                                                            return ` Strain: ${strain.toFixed(1)} (${intensity} Intensity)`;
                                                        },
                                                    },
                                                },
                                            },
                                            scales: {
                                                x: {
                                                    display: true,
                                                    grid: {
                                                        color: isDark ? '#374151' : '#F3F4F6',
                                                        drawOnChartArea: true,
                                                        drawTicks: true,
                                                        lineWidth: 1
                                                    },
                                                    ticks: {
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        font: { size: 12 },
                                                        maxRotation: 45,
                                                        minRotation: 45,
                                                    },
                                                    border: {
                                                        display: false,
                                                    },
                                                },
                                                y: {
                                                    display: true,
                                                    grid: {
                                                        color: isDark ? '#374151' : '#F3F4F6',
                                                        drawOnChartArea: true,
                                                        drawTicks: true,
                                                        lineWidth: 1
                                                    },
                                                    ticks: {
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        font: { size: 12 },
                                                        stepSize: 5,
                                                    },
                                                    border: {
                                                        display: false,
                                                    },
                                                    title: {
                                                        display: true,
                                                        text: 'Strain Score',
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        font: { size: 12 },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                    <DynamicAttribution sources={['whoop']} />
                                </div>
                            </div>
                            </OverlayExportWrapper>
                        )}
                    </>
                )}

                {/* Garmin Data Section */}
                {TERRA_PROVIDER_IDS.some(id => selectedDataSources.includes(id)) && terraData && (
                    <>
                        {/* Debug info for Garmin data - only show if user is admin */}
                        {userInfo?.role === 'admin' && (
                            <div className={`mb-4 p-4 ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-100'} rounded-lg text-xs font-mono`}>
                                <div className="font-bold mb-2">Terra Data Debug:</div>
                                <div>Distance: {terraData.distance_data?.length || 0} points</div>
                                <div>RHR: {terraData.rhr_data?.length || 0} points</div>
                                <div>HR Zones: {terraData.hr_zone_data?.length || 0} points</div>
                                <div>Weekly: {terraData.weekly_data?.length || 0} points</div>
                                <div>Connected: {terraData.terra_connected ? 'Yes' : 'No'}</div>
                                <div>Selected Sources: {selectedDataSources.join(', ')}</div>
                            </div>
                        )}

                        {/* Show message if Terra provider is connected but no data */}
                        {terraData.terra_connected &&
                         (!terraData.distance_data || terraData.distance_data.length === 0) &&
                         (!terraData.rhr_data || terraData.rhr_data.length === 0) &&
                         (!terraData.hr_zone_data || terraData.hr_zone_data.length === 0) &&
                         (!terraData.weekly_data || terraData.weekly_data.length === 0) && (
                            <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl mb-8`}>
                                <div className="text-center">
                                    <h3 className="text-xl font-bold theme-text-primary mb-2">Wearable Connected</h3>
                                    <p className="theme-text-secondary">
                                        Your wearable device is connected, but no data is available for the selected time period.
                                        Try selecting a different date range or check back later.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Daily Mileage Chart - Garmin */}
                        {showCategory('activity') && terraData.distance_data && terraData.distance_data.length > 0 && (
                            <OverlayExportWrapper metricName="daily_mileage" className="mb-8">
                            <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-2xl font-bold theme-text-primary">Daily Mileage</h3>
                                        <p className="theme-text-secondary">{`Your daily distance from ${getTerraSourceLabel(selectedDataSources)}`}</p>
                                    </div>
                                    {terraData.stats && terraData.stats.total_distance_miles > 0 && (
                                        <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl ${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`}>
                                            <TrendingUp className="w-4 h-4" />
                                            <span className="font-semibold">
                                                {terraData.stats.total_distance_miles.toFixed(1)} mi total
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col h-96 mb-6">
                                    <Line
                                        data={{
                                            labels: [...terraData.distance_data]
                                                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                .map(point => new Date(point.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
                                            datasets: [
                                                {
                                                    label: 'Distance',
                                                    data: [...terraData.distance_data]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(point => point.distance_miles),
                                                    borderColor: isDark ? '#22C55E' : '#16A34A',
                                                    backgroundColor: isDark ? 'rgba(34, 197, 94, 0.2)' : 'rgba(22, 163, 74, 0.2)',
                                                    borderWidth: 3,
                                                    pointBackgroundColor: isDark ? '#22C55E' : '#16A34A',
                                                    pointBorderColor: '#ffffff',
                                                    pointBorderWidth: 2,
                                                    pointRadius: 4,
                                                    pointHoverRadius: 6,
                                                    fill: true,
                                                    tension: 0.4,
                                                }
                                            ]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {
                                                    display: false,
                                                },
                                                tooltip: {
                                                    backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
                                                    titleColor: isDark ? '#F9FAFB' : '#111827',
                                                    bodyColor: isDark ? '#D1D5DB' : '#374151',
                                                    borderColor: isDark ? '#374151' : '#E5E7EB',
                                                    borderWidth: 1,
                                                    cornerRadius: 12,
                                                    displayColors: false,
                                                    titleFont: { weight: 'bold' },
                                                    callbacks: {
                                                        label: (context: any) => {
                                                            return ` Distance: ${context.parsed.y.toFixed(2)} mi`;
                                                        },
                                                    },
                                                },
                                            },
                                            scales: {
                                                x: {
                                                    display: true,
                                                    grid: {
                                                        color: isDark ? '#374151' : '#F3F4F6',
                                                    },
                                                    ticks: {
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        font: { size: 12 },
                                                    },
                                                    border: {
                                                        display: false,
                                                    },
                                                },
                                                y: {
                                                    display: true,
                                                    grid: {
                                                        color: isDark ? '#374151' : '#F3F4F6',
                                                    },
                                                    ticks: {
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        font: { size: 12 },
                                                        callback: (value: any) => `${value} mi`,
                                                    },
                                                    border: {
                                                        display: false,
                                                    },
                                                    title: {
                                                        display: true,
                                                        text: 'Distance (miles)',
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        font: { size: 12 },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                    <DynamicAttribution sources={selectedDataSources.filter(s => TERRA_PROVIDER_IDS.includes(s))} />
                                </div>
                            </div>
                            </OverlayExportWrapper>
                        )}

                        {/* Weekly Rolling Average Chart - Garmin */}
                        {showCategory('activity') && terraData.weekly_data && terraData.weekly_data.length > 0 && (
                            <OverlayExportWrapper metricName="weekly_mileage" className="mb-8">
                            <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-2xl font-bold theme-text-primary">Weekly Mileage Rolling Average</h3>
                                        <p className="theme-text-secondary">4-week smoothed trends</p>
                                    </div>
                                </div>
                                <div className="flex flex-col h-96 mb-6">
                                    <Line
                                        data={{
                                            labels: terraData.weekly_data.map(w =>
                                                new Date(w.week_start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                            ),
                                            datasets: [
                                                {
                                                    label: 'Weekly Total',
                                                    data: terraData.weekly_data.map(w => w.weekly_total),
                                                    borderColor: isDark ? '#22C55E' : '#16A34A',
                                                    backgroundColor: isDark ? 'rgba(34, 197, 94, 0.2)' : 'rgba(22, 163, 74, 0.2)',
                                                    borderWidth: 2,
                                                    pointBackgroundColor: isDark ? '#22C55E' : '#16A34A',
                                                    pointBorderColor: '#ffffff',
                                                    pointBorderWidth: 2,
                                                    pointRadius: 4,
                                                    pointHoverRadius: 6,
                                                    fill: true,
                                                    tension: 0.4,
                                                },
                                                {
                                                    label: '4-Week Rolling Avg',
                                                    data: terraData.weekly_data.map(w => w.rolling_average ?? null),
                                                    borderColor: isDark ? '#F59E0B' : '#D97706',
                                                    backgroundColor: 'transparent',
                                                    borderWidth: 3,
                                                    borderDash: [5, 5],
                                                    pointRadius: 0,
                                                    fill: false,
                                                    tension: 0.4,
                                                }
                                            ]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {
                                                    display: true,
                                                    position: 'top' as const,
                                                    labels: {
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        usePointStyle: true,
                                                    }
                                                },
                                                tooltip: {
                                                    backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
                                                    titleColor: isDark ? '#F9FAFB' : '#111827',
                                                    bodyColor: isDark ? '#D1D5DB' : '#374151',
                                                    borderColor: isDark ? '#374151' : '#E5E7EB',
                                                    borderWidth: 1,
                                                    cornerRadius: 12,
                                                    displayColors: true,
                                                    titleFont: { weight: 'bold' },
                                                    callbacks: {
                                                        label: (context: any) => {
                                                            const value = context.parsed.y;
                                                            if (value === null) return '';
                                                            return ` ${context.dataset.label}: ${value.toFixed(2)} mi`;
                                                        },
                                                    },
                                                },
                                            },
                                            scales: {
                                                x: {
                                                    display: true,
                                                    grid: {
                                                        color: isDark ? '#374151' : '#F3F4F6',
                                                    },
                                                    ticks: {
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        font: { size: 12 },
                                                    },
                                                    border: {
                                                        display: false,
                                                    },
                                                },
                                                y: {
                                                    display: true,
                                                    grid: {
                                                        color: isDark ? '#374151' : '#F3F4F6',
                                                    },
                                                    ticks: {
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        font: { size: 12 },
                                                        callback: (value: any) => `${value} mi`,
                                                    },
                                                    border: {
                                                        display: false,
                                                    },
                                                    title: {
                                                        display: true,
                                                        text: 'Distance (miles)',
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        font: { size: 12 },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                    <DynamicAttribution sources={selectedDataSources.filter(s => TERRA_PROVIDER_IDS.includes(s))} />
                                </div>
                            </div>
                            </OverlayExportWrapper>
                        )}

                        {/* Sleep Stages Chart - Garmin only (hidden when combined chart is shown) */}
                        {showCategory('sleep') && terraData.sleep_stages_data && terraData.sleep_stages_data.length > 0 &&
                         !(selectedDataSources.includes('whoop') && sleepData && sleepData.length > 0) && (
                            <OverlayExportWrapper metricName="sleep_stages_garmin" className="mb-8">
                            <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-2xl font-bold theme-text-primary">Sleep Stages</h3>
                                        <p className="theme-text-secondary">{`Breakdown of your sleep stages from ${getTerraSourceLabel(selectedDataSources)}`}</p>
                                    </div>
                                </div>
                                <div className="flex flex-col h-96 mb-6">
                                    <Line
                                        data={{
                                            labels: [...terraData.sleep_stages_data]
                                                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                .map(point => new Date(point.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
                                            datasets: [
                                                {
                                                    label: 'Light Sleep',
                                                    data: [...terraData.sleep_stages_data]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(point => (point.light_sleep_minutes || 0) / 60),
                                                    borderColor: isDark ? '#60A5FA' : '#3B82F6',
                                                    backgroundColor: isDark ? 'rgba(96, 165, 250, 0.6)' : 'rgba(59, 130, 246, 0.6)',
                                                    borderWidth: 0,
                                                    fill: true,
                                                    tension: 0.4,
                                                },
                                                {
                                                    label: 'Deep Sleep',
                                                    data: [...terraData.sleep_stages_data]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(point => (point.deep_sleep_minutes || 0) / 60),
                                                    borderColor: isDark ? '#34D399' : '#10B981',
                                                    backgroundColor: isDark ? 'rgba(52, 211, 153, 0.6)' : 'rgba(16, 185, 129, 0.6)',
                                                    borderWidth: 0,
                                                    fill: true,
                                                    tension: 0.4,
                                                },
                                                {
                                                    label: 'REM Sleep',
                                                    data: [...terraData.sleep_stages_data]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(point => (point.rem_sleep_minutes || 0) / 60),
                                                    borderColor: isDark ? '#F472B6' : '#EC4899',
                                                    backgroundColor: isDark ? 'rgba(244, 114, 182, 0.6)' : 'rgba(236, 72, 153, 0.6)',
                                                    borderWidth: 0,
                                                    fill: true,
                                                    tension: 0.4,
                                                }
                                            ]
                                        }}
                                        options={{
                                            ...getChartOptions(),
                                            scales: {
                                                ...getChartOptions().scales,
                                                y: {
                                                    ...getChartOptions().scales.y,
                                                    stacked: true,
                                                    ticks: {
                                                        ...getChartOptions().scales.y.ticks,
                                                        callback: (value: any) => `${value} hrs`,
                                                    },
                                                },
                                                x: {
                                                    ...getChartOptions().scales.x,
                                                    stacked: true,
                                                }
                                            },
                                            plugins: {
                                                ...getChartOptions().plugins,
                                                tooltip: {
                                                    ...getChartOptions().plugins.tooltip,
                                                    callbacks: {
                                                        title: (context: any) => {
                                                            return `${context[0].label}`;
                                                        },
                                                        label: (context: any) => {
                                                            return ` ${context.dataset.label}: ${context.parsed.y.toFixed(1)} hrs`;
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                    <DynamicAttribution sources={selectedDataSources.filter(s => TERRA_PROVIDER_IDS.includes(s))} />
                                </div>
                            </div>
                            </OverlayExportWrapper>
                        )}

                        {/* HR Zone Breakdown - Garmin */}
                        {showCategory('cardio') && terraData.hr_zone_data && terraData.hr_zone_data.length > 0 && (
                            <OverlayExportWrapper metricName="hr_zones" className="mb-8">
                            <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl`}>
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex flex-col">
                                        <h3 className="text-2xl font-bold theme-text-primary">Time in HR Zones</h3>
                                        <p className="theme-text-secondary">Daily heart rate zone distribution</p>
                                        <div className="flex items-center space-x-4 mt-2 flex-wrap gap-y-1">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-3 h-3 rounded" style={{ backgroundColor: isDark ? '#cfcfcf' : '#8a8a8a' }}></div>
                                                <span className="text-xs theme-text-secondary">Z1 Recovery</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#3B82F6' }}></div>
                                                <span className="text-xs theme-text-secondary">Z2 Aerobic</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#22C55E' }}></div>
                                                <span className="text-xs theme-text-secondary">Z3 Tempo</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#F97316' }}></div>
                                                <span className="text-xs theme-text-secondary">Z4 Threshold</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#EF4444' }}></div>
                                                <span className="text-xs theme-text-secondary">Z5 VO2max</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex flex-col h-96 mb-6">
                                    <Bar
                                        data={{
                                            labels: [...terraData.hr_zone_data]
                                                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                .map(point => new Date(point.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
                                            datasets: [
                                                {
                                                    label: 'Zone 1 (Recovery)',
                                                    data: [...terraData.hr_zone_data]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(d => d.zone_1_minutes || 0),
                                                    backgroundColor: isDark ? '#cfcfcf' : '#8a8a8a',
                                                    borderWidth: 0,
                                                    borderRadius: 2,
                                                },
                                                {
                                                    label: 'Zone 2 (Aerobic)',
                                                    data: [...terraData.hr_zone_data]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(d => d.zone_2_minutes || 0),
                                                    backgroundColor: '#3B82F6',
                                                    borderWidth: 0,
                                                    borderRadius: 2,
                                                },
                                                {
                                                    label: 'Zone 3 (Tempo)',
                                                    data: [...terraData.hr_zone_data]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(d => d.zone_3_minutes || 0),
                                                    backgroundColor: '#22C55E',
                                                    borderWidth: 0,
                                                    borderRadius: 2,
                                                },
                                                {
                                                    label: 'Zone 4 (Threshold)',
                                                    data: [...terraData.hr_zone_data]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(d => d.zone_4_minutes || 0),
                                                    backgroundColor: '#F97316',
                                                    borderWidth: 0,
                                                    borderRadius: 2,
                                                },
                                                {
                                                    label: 'Zone 5 (VO2max)',
                                                    data: [...terraData.hr_zone_data]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(d => d.zone_5_minutes || 0),
                                                    backgroundColor: '#EF4444',
                                                    borderWidth: 0,
                                                    borderRadius: 2,
                                                },
                                            ]
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {
                                                    display: false, // Using custom legend above
                                                },
                                                tooltip: {
                                                    backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
                                                    titleColor: isDark ? '#F9FAFB' : '#111827',
                                                    bodyColor: isDark ? '#D1D5DB' : '#374151',
                                                    borderColor: isDark ? '#374151' : '#E5E7EB',
                                                    borderWidth: 1,
                                                    cornerRadius: 12,
                                                    displayColors: true,
                                                    titleFont: { weight: 'bold' },
                                                    callbacks: {
                                                        label: (context: any) => {
                                                            const value = context.parsed.y;
                                                            return ` ${context.dataset.label}: ${value.toFixed(0)} min`;
                                                        },
                                                    },
                                                },
                                            },
                                            scales: {
                                                x: {
                                                    stacked: true,
                                                    display: true,
                                                    grid: {
                                                        color: isDark ? '#374151' : '#F3F4F6',
                                                    },
                                                    ticks: {
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        font: { size: 12 },
                                                    },
                                                    border: {
                                                        display: false,
                                                    },
                                                },
                                                y: {
                                                    stacked: true,
                                                    display: true,
                                                    grid: {
                                                        color: isDark ? '#374151' : '#F3F4F6',
                                                    },
                                                    ticks: {
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        font: { size: 12 },
                                                        callback: (value: any) => `${value} min`,
                                                    },
                                                    border: {
                                                        display: false,
                                                    },
                                                    title: {
                                                        display: true,
                                                        text: 'Time (minutes)',
                                                        color: isDark ? '#9CA3AF' : '#6B7280',
                                                        font: { size: 12 },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                    <DynamicAttribution sources={selectedDataSources.filter(s => TERRA_PROVIDER_IDS.includes(s))} />
                                </div>
                            </div>
                            </OverlayExportWrapper>
                        )}

                        {/* Respiratory Rate Trend */}
                        {showCategory('cardio') && terraData.respiratory_rate_data && terraData.respiratory_rate_data.length > 0 && (
                            <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl mb-8`}>
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-2xl font-bold theme-text-primary">Respiratory Rate</h3>
                                        <p className="theme-text-secondary">Average breathing rate during sleep (breaths/min)</p>
                                    </div>
                                    {terraData.respiratory_rate_data.length > 0 && (
                                        <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                                            <span className="font-semibold">
                                                {terraData.respiratory_rate_data[terraData.respiratory_rate_data.length - 1].breaths_per_min} br/min
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col h-96 mb-6">
                                    <Line
                                        data={{
                                            labels: [...terraData.respiratory_rate_data]
                                                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                .map(point => new Date(point.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
                                            datasets: [
                                                {
                                                    label: 'Resp Rate',
                                                    data: [...terraData.respiratory_rate_data]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(point => point.breaths_per_min),
                                                    borderColor: isDark ? '#8B5CF6' : '#7C3AED',
                                                    backgroundColor: isDark ? 'rgba(139, 92, 246, 0.2)' : 'rgba(124, 58, 237, 0.2)',
                                                    fill: true,
                                                    tension: 0.4,
                                                    pointRadius: 3,
                                                    pointHoverRadius: 6,
                                                },
                                            ],
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                y: {
                                                    suggestedMin: 10,
                                                    suggestedMax: 22,
                                                    ticks: { color: isDark ? '#9CA3AF' : '#6B7280' },
                                                    grid: { color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
                                                    title: { display: true, text: 'Breaths/min', color: isDark ? '#9CA3AF' : '#6B7280', font: { size: 12 } },
                                                },
                                                x: {
                                                    ticks: { color: isDark ? '#9CA3AF' : '#6B7280', maxRotation: 45 },
                                                    grid: { display: false },
                                                },
                                            },
                                        }}
                                    />
                                    <DynamicAttribution sources={selectedDataSources.filter(s => TERRA_PROVIDER_IDS.includes(s))} />
                                </div>
                            </div>
                        )}

                        {/* Body Temperature Deviation — only render when baseline is available (deviation !== null) */}
                        {showCategory('body') && terraData.body_temp_data && terraData.body_temp_data.some(d => d.deviation != null) && (
                            <div className={`cgm-card rounded-2xl p-8 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl mb-8`}>
                                <div className="flex items-center justify-between mb-6">
                                    <div>
                                        <h3 className="text-2xl font-bold theme-text-primary">Body Temperature</h3>
                                        <p className="theme-text-secondary">Deviation from your personal baseline</p>
                                    </div>
                                    {(() => {
                                        const latest = terraData.body_temp_data[terraData.body_temp_data.length - 1];
                                        const dev = latest?.deviation;
                                        if (dev == null) return null;
                                        const isElevated = dev > 0.3;
                                        return (
                                            <div className={`flex items-center space-x-2 px-4 py-2 rounded-xl ${
                                                isElevated
                                                    ? (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800')
                                                    : (isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800')
                                            }`}>
                                                <span className="font-semibold">
                                                    {dev > 0 ? '+' : ''}{dev.toFixed(2)}&deg;C
                                                </span>
                                            </div>
                                        );
                                    })()}
                                </div>
                                <div className="flex flex-col h-96 mb-6">
                                    <Line
                                        data={{
                                            labels: [...terraData.body_temp_data]
                                                .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                .map(point => new Date(point.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
                                            datasets: [
                                                {
                                                    label: 'Temp Deviation',
                                                    data: [...terraData.body_temp_data]
                                                        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                                                        .map(point => point.deviation),
                                                    borderColor: isDark ? '#F97316' : '#EA580C',
                                                    backgroundColor: isDark ? 'rgba(249, 115, 22, 0.15)' : 'rgba(234, 88, 12, 0.08)',
                                                    fill: true,
                                                    tension: 0.4,
                                                    pointRadius: 3,
                                                    pointHoverRadius: 6,
                                                },
                                                {
                                                    label: 'Baseline',
                                                    data: [...terraData.body_temp_data].map(() => 0),
                                                    borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                                                    borderDash: [5, 5],
                                                    pointRadius: 0,
                                                    fill: false,
                                                },
                                            ],
                                        }}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                y: {
                                                    suggestedMin: -0.5,
                                                    suggestedMax: 1.0,
                                                    ticks: { color: isDark ? '#9CA3AF' : '#6B7280' },
                                                    grid: { color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
                                                    title: { display: true, text: 'Deviation (\u00B0C)', color: isDark ? '#9CA3AF' : '#6B7280', font: { size: 12 } },
                                                },
                                                x: {
                                                    ticks: { color: isDark ? '#9CA3AF' : '#6B7280', maxRotation: 45 },
                                                    grid: { display: false },
                                                },
                                            },
                                        }}
                                    />
                                    <DynamicAttribution sources={selectedDataSources.filter(s => TERRA_PROVIDER_IDS.includes(s))} />
                                </div>
                            </div>
                        )}
                    </>
                )}

                {selectedDataSources.includes('whoop') && isDataEmpty && (
                    <div className={`cgm-card rounded-2xl p-12 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl text-center overflow-hidden relative`}>
                        {/* Decorative gradient background */}
                        <div className="absolute inset-0" style={{ backgroundColor: 'var(--accent-ui-subtle)' }}></div>

                        <div className="relative z-10">
                            {/* Icon with gradient background */}
                            <div className="w-28 h-28 rounded-full flex items-center justify-center mx-auto mb-6 border-2" style={{ backgroundColor: 'var(--accent-ui-subtle)', borderColor: 'var(--accent-ui-border)' }}>
                                <HeartPulse className={`w-14 h-14 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                            </div>

                            {userInfo?.whoop_connected ? (
                            <div className="space-y-6">
                                <div className={`${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border ${isDark ? 'border-blue-700' : 'border-blue-200'} rounded-xl p-6`}>
                                    <div className="flex items-center justify-center space-x-2 mb-3">
                                        <svg className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className={`font-semibold ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>Device Connected!</span>
                                    </div>
                                    <p className={`${isDark ? 'text-blue-300' : 'text-blue-700'} text-sm leading-relaxed`}>
                                        Your Whoop device is connected and we're syncing your data.
                                        This usually takes 5-10 minutes for new connections.
                                    </p>
                                </div>

                                <p className="text-lg theme-text-secondary max-w-2xl mx-auto leading-relaxed">
                                    If you just connected your device, please wait a few minutes for data to sync.
                                    Historical data from the past 30 days will be imported automatically.
                                </p>

                                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                    <button
                                        onClick={triggerDataSync}
                                        className="inline-flex items-center space-x-2 cgm-button px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                <span>Syncing from WHOOP...</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                <span>Sync from WHOOP</span>
                                            </>
                                        )}
                                    </button>

                                    <Link
                                        to="/connect-whoop"
                                        className={`inline-flex items-center space-x-2 ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} px-6 py-3 rounded-xl font-semibold transition-all duration-200`}
                                    >
                                        <Settings className="w-5 h-5" />
                                        <span>Reconnect Device</span>
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-3xl font-bold cgm-text-primary mb-3">
                                        Connect Your Device
                                    </h3>
                                    <p className="text-xl theme-text-secondary mb-2 max-w-xl mx-auto font-medium">
                                        Please connect your device to see readiness insights
                                    </p>
                                    <p className="text-base theme-text-secondary max-w-lg mx-auto leading-relaxed">
                                        Track your heart rate variability, recovery scores, and get personalized insights to optimize your performance.
                                    </p>
                                </div>
                                <Link
                                    to="/connect-whoop"
                                    className="inline-flex items-center space-x-3 cgm-button px-8 py-4 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg text-lg"
                                >
                                    <Watch className="w-6 h-6" />
                                    <span>Connect Device</span>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                </Link>
                            </div>
                        )}
                        </div>
                    </div>
                )}
            </DashboardLayout>

            {/* Debug Panel - Fixed at bottom right (Admin only) */}
            {userInfo?.role === 'admin' && (
                <div className="fixed bottom-4 right-4 z-50">
                    <button
                        onClick={() => setShowDebugPanel(!showDebugPanel)}
                        className={`mb-2 px-3 py-2 rounded-lg text-xs font-mono ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} transition-colors shadow-lg`}
                    >
                        🐛 Debug
                    </button>

                    {showDebugPanel && (
                        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-600' : 'border-gray-300'} rounded-lg shadow-2xl p-4 w-80 max-h-96 overflow-y-auto`}>
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-bold text-sm theme-text-primary">API Debug Info</h4>
                                <button
                                    onClick={() => setShowDebugPanel(false)}
                                    className="text-gray-400 hover:text-gray-600 text-xs"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="space-y-3 text-xs font-mono">
                                <div>
                                    <div className="font-semibold theme-text-primary mb-1">Network Status:</div>
                                    <div className={`px-2 py-1 rounded ${debugInfo.networkStatus.includes('Connected')
                                        ? `${isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`
                                        : debugInfo.networkStatus.includes('Error') || debugInfo.networkStatus.includes('Failed')
                                            ? `${isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'}`
                                            : `${isDark ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800'}`
                                        }`}>
                                        {debugInfo.networkStatus}
                                    </div>
                                </div>

                                {debugInfo.lastHrvCall && (
                                    <div>
                                        <div className="font-semibold theme-text-primary mb-1">Last HRV Call:</div>
                                        <div className="theme-text-secondary">
                                            <div>Time: {debugInfo.lastHrvCall.timestamp}</div>
                                            <div>Duration: {debugInfo.lastHrvCall.duration}ms</div>
                                            <div className={
                                                debugInfo.lastHrvCall.status.includes('ERROR')
                                                    ? `${isDark ? 'text-red-400' : 'text-red-600'}`
                                                    : debugInfo.lastHrvCall.status.includes('200')
                                                        ? `${isDark ? 'text-green-400' : 'text-green-600'}`
                                                        : `${isDark ? 'text-yellow-400' : 'text-yellow-600'}`
                                            }>
                                                Status: {debugInfo.lastHrvCall.status}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {debugInfo.lastSleepCall && (
                                    <div>
                                        <div className="font-semibold theme-text-primary mb-1">Last Sleep Call:</div>
                                        <div className="theme-text-secondary">
                                            <div>Time: {debugInfo.lastSleepCall.timestamp}</div>
                                            <div>Duration: {debugInfo.lastSleepCall.duration}ms</div>
                                            <div className={
                                                debugInfo.lastSleepCall.status.includes('ERROR')
                                                    ? `${isDark ? 'text-red-400' : 'text-red-600'}`
                                                    : debugInfo.lastSleepCall.status.includes('200')
                                                        ? `${isDark ? 'text-green-400' : 'text-green-600'}`
                                                        : `${isDark ? 'text-yellow-400' : 'text-yellow-600'}`
                                            }>
                                                Status: {debugInfo.lastSleepCall.status}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {debugInfo.lastActivityCall && (
                                    <div>
                                        <div className="font-semibold theme-text-primary mb-1">Last Activity Call:</div>
                                        <div className="theme-text-secondary">
                                            <div>Time: {debugInfo.lastActivityCall.timestamp}</div>
                                            <div>Duration: {debugInfo.lastActivityCall.duration}ms</div>
                                            <div className={
                                                debugInfo.lastActivityCall.status.includes('ERROR')
                                                    ? `${isDark ? 'text-red-400' : 'text-red-600'}`
                                                    : debugInfo.lastActivityCall.status.includes('200')
                                                        ? `${isDark ? 'text-green-400' : 'text-green-600'}`
                                                        : `${isDark ? 'text-yellow-400' : 'text-yellow-600'}`
                                            }>
                                                Status: {debugInfo.lastActivityCall.status}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {debugInfo.lastUserCall && (
                                    <div>
                                        <div className="font-semibold theme-text-primary mb-1">Last User Call:</div>
                                        <div className="theme-text-secondary">
                                            <div>Time: {debugInfo.lastUserCall.timestamp}</div>
                                            <div>Duration: {debugInfo.lastUserCall.duration}ms</div>
                                            <div className={
                                                debugInfo.lastUserCall.status.includes('ERROR')
                                                    ? `${isDark ? 'text-red-400' : 'text-red-600'}`
                                                    : debugInfo.lastUserCall.status.includes('200')
                                                        ? `${isDark ? 'text-green-400' : 'text-green-600'}`
                                                        : `${isDark ? 'text-yellow-400' : 'text-yellow-600'}`
                                            }>
                                                Status: {debugInfo.lastUserCall.status}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="border-t pt-2 space-y-2">
                                    <button
                                        onClick={testConnectivity}
                                        className="w-full px-2 py-1 rounded text-xs cgm-button transition-all duration-200 shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.15)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)]"
                                    >
                                        Test Connection
                                    </button>

                                    <button
                                        onClick={triggerDataSync}
                                        className="w-full px-2 py-1 rounded text-xs cgm-button transition-all duration-200 shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.15)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)]"
                                        disabled={loading}
                                    >
                                        {loading ? 'Syncing...' : 'Sync via Celery'}
                                    </button>

                                    <button
                                        onClick={refreshUserInfo}
                                        className="w-full px-2 py-1 rounded text-xs cgm-button transition-all duration-200 shadow-[inset_0_1px_2px_rgba(0,0,0,0.1)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.15)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[inset_0_1px_2px_rgba(0,0,0,0.15)]"
                                    >
                                        Refresh User Info
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Dashboard;
